// Poketrace Price Service - Core module
// Handles ID resolution, price fetching, and Supabase caching.
// Multi-source pricing: Poketrace + TCGdex + Gemini AI analysis.

import { supabase } from '../lib/supabase';
import { poketraceGet } from '../lib/vendorProxy';
import { geminiGenerate } from '../lib/geminiProxy';
import { fetchTCGdexPricing, getTCGdexBestPrice } from './tcgdexPricing';
import { fetchJustTCGPricing } from './justTCGPricing';
import { isVintageSet } from './vintageDetection';
import { resolveSetAlias } from './pricingAliases';
import { matchConfig, loadMatchConfig, demoteRules } from './matchConfig';
import { FAILURE_STAGES, reportPricingFailure } from './pricingReports';

// Requests go through the poketrace-proxy edge function, which holds the key in
// project secrets. The key used to be read here as
// REACT_APP_POKETRACE_API_KEY, and a CRA env var is inlined into the bundle at
// build time, so it shipped inside the app where anyone could read it.
//
// API_BASE stays only as documentation of what the paths below are relative to;
// nothing fetches it directly any more.
const API_BASE = '';

// Whether the pricing vendor is reachable at all. It used to be "is the API key
// present", which was a client-side test of a client-side key. The key is now
// server-side, and what the client needs to know is only whether it has a
// server to ask, so this tracks the proxy's precondition instead.
const VENDOR_READY = Boolean(process.env.REACT_APP_SUPABASE_URL);

// !! DO NOT CHANGE without asking Chase first !!
// Bump this integer when search/pricing logic changes to auto-invalidate stale cache.
// All cached rows with a different version are treated as stale and re-fetched.
// Stored in Supabase as "v{N}" (e.g. "v2") in the research_version column.
export const RESEARCH_CACHE_VERSION = 3;

/**
 * Cache freshness.
 *
 * The cache had no TTL at all: a price was shown regardless of age and only
 * refreshed if someone pressed "Refresh Price" by hand. For a trading app a
 * stale price is worse than a slow one, so cached rows now expire.
 *
 * Modern cards move quickly and are cheap to re-fetch. Vintage cards trade
 * rarely, so their numbers stay meaningful far longer and re-fetching them
 * constantly just burns API credits for no new information.
 */
export const CACHE_TTL_DAYS = { modern: 7, vintage: 30 };

export function isCacheStale(fetchedAt, setName) {
  if (!fetchedAt) return true;
  const ageDays = (Date.now() - new Date(fetchedAt).getTime()) / 86400000;
  if (Number.isNaN(ageDays)) return true;
  const ttl = isVintageSet(setName) ? CACHE_TTL_DAYS.vintage : CACHE_TTL_DAYS.modern;
  return ageDays > ttl;
}

// Grade mapping: user-facing grade string -> Poketrace tier key
const CONDITION_MAP = {
  'NM': 'NEAR_MINT',
  'Near Mint': 'NEAR_MINT',
  'nm': 'NEAR_MINT',
  'LP': 'LIGHTLY_PLAYED',
  'Lightly Played': 'LIGHTLY_PLAYED',
  'lp': 'LIGHTLY_PLAYED',
  'MP': 'MODERATELY_PLAYED',
  'Moderately Played': 'MODERATELY_PLAYED',
  'mp': 'MODERATELY_PLAYED',
  'HP': 'HEAVILY_PLAYED',
  'Heavily Played': 'HEAVILY_PLAYED',
  'hp': 'HEAVILY_PLAYED',
  'DMG': 'DAMAGED',
  'Damaged': 'DAMAGED',
  'damaged': 'DAMAGED',
  'Ungraded': 'NEAR_MINT',
  'ungraded': 'NEAR_MINT',
};

// Condition multipliers as fallback when a specific tier is missing from Poketrace
const CONDITION_MULTIPLIERS = {
  'NEAR_MINT': 1.0,
  'LIGHTLY_PLAYED': 0.8,
  'MODERATELY_PLAYED': 0.6,
  'HEAVILY_PLAYED': 0.4,
  'DAMAGED': 0.25,
};

// Legacy condition multipliers (same keys as old priceService)
const LEGACY_CONDITION_MULTIPLIERS = {
  'nm': 1.0,
  'lp': 0.85,
  'mp': 0.65,
  'hp': 0.45,
  'damaged': 0.20,
};

// Raw condition tiers (non-graded)
const RAW_TIERS = ['NEAR_MINT', 'LIGHTLY_PLAYED', 'MODERATELY_PLAYED', 'HEAVILY_PLAYED', 'DAMAGED'];

// Graded sort order
const GRADE_SORT = [
  'PSA_10', 'PSA_9', 'PSA_8', 'PSA_7', 'PSA_6', 'PSA_5', 'PSA_4', 'PSA_3', 'PSA_2', 'PSA_1',
  'CGC_10', 'CGC_9_5', 'CGC_9', 'CGC_8_5', 'CGC_8', 'CGC_7_5', 'CGC_7', 'CGC_6_5', 'CGC_6',
  'CGC_5_5', 'CGC_5', 'CGC_4_5', 'CGC_4', 'CGC_3_5', 'CGC_3', 'CGC_2_5', 'CGC_2', 'CGC_1_5', 'CGC_1',
  'BGS_10', 'BGS_9_5', 'BGS_9', 'BGS_8_5', 'BGS_8', 'BGS_7_5', 'BGS_7', 'BGS_6_5', 'BGS_6',
  'BGS_5_5', 'BGS_5', 'BGS_4_5', 'BGS_4', 'BGS_3_5', 'BGS_3', 'BGS_2_5', 'BGS_2',
  'SGC_10', 'SGC_9_5', 'SGC_9', 'SGC_8_5', 'SGC_8', 'SGC_7_5', 'SGC_7', 'SGC_6_5', 'SGC_6',
  'SGC_5_5', 'SGC_5', 'SGC_4_5', 'SGC_4', 'SGC_3_5', 'SGC_3', 'SGC_2_5', 'SGC_2',
  'ACE_10', 'ACE_9', 'ACE_8', 'ACE_7', 'ACE_6', 'ACE_5', 'ACE_4', 'ACE_3', 'ACE_2',
  'TAG_10', 'TAG_9_5', 'TAG_9', 'TAG_8_5', 'TAG_8', 'TAG_7_5', 'TAG_7', 'TAG_6_5',
  'TAG_6', 'TAG_5_5', 'TAG_5', 'TAG_4_5', 'TAG_4', 'TAG_3_5', 'TAG_3', 'TAG_2_5',
  'TAG_2', 'TAG_1_5', 'TAG_1',
];

// ---- Poketrace API ----

/**
 * Convert a user-facing grade like "PSA 10" or "CGC 9.5" to a Poketrace key like "PSA_10" or "CGC_9_5"
 */
export function gradeToPoketraceKey(grade) {
  if (!grade) return null;

  // Check if it's a raw condition
  const conditionKey = CONDITION_MAP[grade];
  if (conditionKey) return conditionKey;

  // Also check case-insensitive condition keywords
  const gradeLower = grade.toLowerCase();
  if (gradeLower.includes('damaged') || gradeLower.includes('dmg')) return 'DAMAGED';
  if (gradeLower.includes('heavily') || gradeLower === 'hp') return 'HEAVILY_PLAYED';
  if (gradeLower.includes('moderately') || gradeLower === 'mp') return 'MODERATELY_PLAYED';
  if (gradeLower.includes('lightly') || gradeLower === 'lp') return 'LIGHTLY_PLAYED';
  if (gradeLower.includes('near mint') || gradeLower.includes('mint') || gradeLower === 'nm') return 'NEAR_MINT';

  // Graded: "PSA 10" -> "PSA_10", "CGC 9.5" -> "CGC_9_5"
  const parts = grade.trim().split(/\s+/);
  if (parts.length < 2) return null;

  const company = parts[0].toUpperCase();
  const number = parts.slice(1).join('').replace(/\./g, '_');
  return `${company}_${number}`;
}

/**
 * Check if a grade string represents a graded card (vs raw condition)
 */
export function isGradedCard(grade) {
  if (!grade) return false;
  const gradeLower = grade.toLowerCase();
  return gradeLower.includes('psa') ||
         gradeLower.includes('cgc') ||
         gradeLower.includes('bgs') ||
         gradeLower.includes('sgc') ||
         gradeLower.includes('tag') ||
         gradeLower.includes('ace');
}

/**
 * Every vendor call, with one polite retry when we are told we are going too
 * fast.
 *
 * A 429 was recorded as `search_error` and the card was written off with a retry
 * window, so a burst of speed on the primer's part produced cards that looked
 * broken to the repair loop: three of the four matcher issues filed against Team
 * Rocket were this and not a matching problem at all. The server copy has
 * retried once since it was written; this is the same wait in the same place.
 */
async function ptGet(path) {
  const res = await poketraceGet(path);
  if (res.status !== 429) return res;
  await new Promise((r) => setTimeout(r, 4000));
  return poketraceGet(path);
}

/**
 * Normalize card number for comparison: "4/102" and "004/102" both become "4/102"
 */
/** "bw04" -> "4", "sv065" -> "65", "17" -> "17". The number with its letters and zeros gone. */
function bareNumber(norm) {
  const head = norm.split('/')[0];
  return head.replace(/^[a-z]+[-\s]?/, '').replace(/^0+/, '') || (head ? '0' : '');
}
function hasNumberPrefix(norm) {
  return /^[a-z]/.test(norm.split('/')[0]);
}

function normalizeCardNumber(num) {
  if (!num) return '';
  // Strip leading zeros from each part: "005/025" -> "5/25"
  return num.split('/').map(p => p.replace(/^0+/, '') || '0').join('/').toLowerCase();
}

/**
 * Search Poketrace for a card by name and match by card number + set
 */
/**
 * Reduce any variant string to a canonical finish so two sides can be compared
 * exactly instead of by substring.
 *
 * Substring matching was actively wrong here: "Holo" is a substring of
 * "Reverse_Holofoil", so a holo card happily matched the reverse holo entry.
 * On Aquapolis Suicune that is the difference between $443 and $101 raw, and
 * $2,162 against $800 in a PSA 9. Order matters below - reverse must be tested
 * before holo for the same reason.
 */
/**
 * Is this string saying 1st EDITION, rather than merely containing "1st"?
 *
 * The loose test read our "1st Movie Stamp" as a first edition and sent 16
 * printings looking for a 1st edition listing nothing has. The vendor always
 * writes the edition out ("1st_Edition_Holofoil"), and so do our own labels
 * ("Holo / Shadowless / 1st Edition Stamp"), so requiring the word costs nothing.
 * Input is already lowercased with separators stripped.
 */
function isFirstEdition(v) {
  return v.includes('1stedition') || v.includes('firstedition') || v.includes('1sted');
}

export function canonicalFinish(variant) {
  const v = (variant || '').toLowerCase().replace(/[_\s-]+/g, '');
  if (!v) return null;
  if (v.includes('reverse')) return 'reverse';
  // "1st" on its own is not 1st edition. Our catalogue carries a 1st Movie stamp
  // on 16 printings, and reading those as 1st edition sent every one of them
  // looking for a 1st edition listing that does not exist.
  if (isFirstEdition(v)) return 'first';
  if (v.includes('shadowless')) return 'shadowless';
  if (v.includes('holo')) return 'holo';
  if (v.includes('normal')) return 'normal';
  // The vendor's word for a WOTC-era non-holo card is "Unlimited", where our
  // catalogue says "Normal": Team Rocket Magikarp 47 exists there only as
  // "Unlimited" and "1st_Edition", so asking for a Normal rejected both and the
  // card died at the finish gate with its real listing sitting in the pool.
  // Reached only when nothing above matched, so "Unlimited_Holofoil" is still a
  // holo and our own "Normal / Unlimited" is still normal.
  if (v.includes('unlimited')) return 'normal';
  return v;
}

/**
 * The stamp on a card, which is a separate axis from its finish.
 *
 * `canonicalFinish` returns one token for something that has two independent
 * parts, and a card can only be one of them at a time. "Holo / Prerelease
 * Stamp" has no clause of its own up there, so it falls through to `holo` and
 * matches the ordinary card: a Team Rocket prerelease Dark Gyarados, one of the
 * most valuable WOTC promos there is, priced at $121 off 180 unlimited sales
 * with four of them mentioning prerelease. A Prismatic Evolutions Espeon ex
 * with a set logo stamp got a byte-identical copy of the unstamped card's
 * comps: same 200 sales, same $22.33 average, same $116.34 top sale.
 *
 * 1st edition is the one that works today, because it happens to sit above
 * `holo` in that list and the vendor sells "1st_Edition_Holofoil" as its own
 * product. That is the shape every stamp needs, not a special case.
 *
 * Read off the composed variant label rather than v_stamps because the label is
 * what crosses into pricing; the axes are only kept apart on the card row.
 */
export function canonicalStamp(variant) {
  const v = (variant || '').toLowerCase().replace(/[_\s-]+/g, '');
  if (!v) return null;
  if (isFirstEdition(v)) return 'first';
  if (v.includes('shadowless')) return 'shadowless';
  if (v.includes('prerelease')) return 'prerelease';
  if (v.includes('staff')) return 'staff';
  if (v.includes('setlogo')) return 'setlogo';
  if (v.includes('pokeball') || v.includes('pokéball')) return 'pokeball';
  if (v.includes('masterball')) return 'masterball';
  // The movie stamps, which the loose "1st" test above used to swallow whole.
  if (v.includes('movie')) return 'movie';
  return null;
}

/**
 * The stamp the vendor recorded, wherever it decided to put it.
 *
 * Three fields carry it and never the same one twice: the variant
 * ("1st_Edition_Holofoil"), the set name ("Base Set (Shadowless)") and the card
 * name ("Dark Gyarados (Prerelease)"). The prerelease Dark Gyarados is filed
 * under "WoTC Promo" with an unremarkable Holofoil variant, so the only place
 * the word appears at all is the name.
 *
 * The name is read from its parenthesised suffix only, never the whole string.
 * A bare name goes through `canonicalStamp` intact and the Master Ball and Poke
 * Ball trainers would come back carrying a stamp they do not have, which the
 * gate below then rejects for every card that wants no stamp - two real cards
 * unpriceable to fix one.
 */
export function vendorStamp(card) {
  const inParens = String(card?.name || '').match(/\(([^)]*)\)/);
  return canonicalStamp(card?.variant)
    || canonicalStamp(card?.set?.name)
    || (inParens ? canonicalStamp(inParens[1]) : null);
}

/**
 * Which WOTC print run this is: first edition, shadowless, or unlimited.
 *
 * A separate axis again, and the one the vendor splits across two fields: the
 * shadowless Charizard is set "Base Set (Shadowless)" with variant
 * "Unlimited_Holofoil", where "Unlimited" means only "not 1st edition". Reading
 * the variant alone calls that card unlimited and prices a $2,900 shadowless
 * copy as the $660 one.
 *
 * Ranked, because the runs nest: a 1st edition card is also shadowless, so the
 * narrowest true statement wins.
 */
export function canonicalPrintRun(...fields) {
  const v = fields.filter(Boolean).join(' ').toLowerCase().replace(/[_\s-]+/g, '');
  if (!v) return null;
  if (isFirstEdition(v)) return 'first';
  if (v.includes('shadowless')) return 'shadowless';
  if (v.includes('unlimited')) return 'unlimited';
  return null;
}

/**
 * Find the vendor's row for a card.
 *
 * Returns { card, failure } rather than a bare card. A null match used to be
 * indistinguishable from "the vendor has never heard of this card", which meant
 * every investigation started from scratch. The failure object names the gate
 * that rejected it and carries what the vendor actually sent back, which is the
 * whole input to the pricing repair loop.
 */
/**
 * When candidates tie, how closely does this one's name match the card we asked
 * for? Lower is better.
 *
 * Vendors list variants of the same number under decorated names: "Charizard"
 * and "Charizard (Black Dot Error)" are both Base Set holo 004/102, and
 * "Riolu - 010" sits beside "Riolu - 010 (Pokemon Center Exclusive)". Scoring
 * cannot separate them because every gate we have treats them as identical.
 *
 * The plain name is the right default. It is overwhelmingly the more common
 * card, and refusing to price it left every ordinary Base Set Charizard with no
 * number at all. Whenever this breaks a tie the screen says which printing it
 * priced, so anyone holding the unusual one can see it and disregard.
 */
function nameSpecificity(candidateName, cardName) {
  const norm = (n) => (n || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (norm(candidateName) === norm(cardName)) return 0;
  if (!/\(/.test(candidateName || '')) return 1;
  return 2;
}

/**
 * Is this candidate the card we asked for, whatever the vendor decorated it with?
 *
 * nameSpecificity above ranks how a name is punctuated, and says nothing about
 * whose name it is: a bare "Grubbin" scores better against "Lycanroc" than
 * "Lycanroc (#16 Non-Holo)" does. That is harmless while every candidate is the
 * same card, and wrong the moment they are not. On a merged trainer kit the
 * vendor files two half-decks as one set, so #16 is Grubbin in one half and
 * Lycanroc in the other, both survive every gate, and the tie-break hands back
 * the wrong Pokemon at a confident price. This gate runs first so only real
 * printings of the asked-for card are ever eligible to win.
 *
 * The prefix has to break on a word boundary. Without that "Mew" claims
 * "Mewtwo" and the cure is worse than the disease.
 */
/** Accents off. "Flabébé" is the vendor's "Flabebe" and must not fail on one byte. */
const deaccent = (n) => String(n || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
/**
 * A card name reduced to words. The leading "Basic" goes, because the vendor
 * prefixes every basic energy with it and our catalogue never does.
 */
const nameWords = (n) => deaccent(n).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  .replace(/^basic /, '');
const nameSquashed = (n) => deaccent(n).toLowerCase().replace(/[^a-z0-9]+/g, '').replace(/^basic/, '');

/**
 * Our "Drapion 4" is the vendor's "Drapion E4": the DP-era numbered reprints
 * carry a letter there on one side and not the other, exactly as the card
 * numbers do. Only when the rest of the name is identical, the digits agree, and
 * precisely one side has the letter.
 */
function numberedNameAgrees(a, b) {
  const split = (s) => {
    const m = s.match(/^(.*?) ([a-z]?)(\d+)$/);
    return m ? { head: m[1], letter: m[2], num: m[3] } : null;
  };
  const x = split(a);
  const y = split(b);
  if (!x || !y) return false;
  return x.head === y.head && x.num === y.num && (x.letter === '') !== (y.letter === '');
}

export function isSameCard(candidateName, cardName) {
  const a = nameWords(candidateName);
  const b = nameWords(cardName);
  if (!a || !b) return false;
  if (a === b) return true;
  // Punctuation only: "Farfetch'd" against "Farfetchd", "Poke Ball" against
  // "Pokeball". Equality, never a prefix, or "Mew" claims "Mewtwo" again.
  if (nameSquashed(candidateName) === nameSquashed(cardName)) return true;
  if (a.startsWith(`${b} `) || b.startsWith(`${a} `)) return true;
  return numberedNameAgrees(a, b);
}


// Words that carry no identity of their own in a set name: era codes Poketrace
// prefixes with, and the generic nouns it appends. "Returns" is deliberately
// absent, so Team Rocket still does not match Team Rocket Returns.
const setFiller = () => new Set(matchConfig().setFiller || []);

const setWords = (name) => {
  // "SV01:" and "SWSH03" carry the same era code with a volume number glued on.
  const eras = (matchConfig().eraCodes || []).join('|');
  return String(name || '')
    .toLowerCase()
    // Strip accents before splitting. The split treats anything outside a-z0-9
    // as a separator, so "pokémon" became ["pok", "mon"] and could never match
    // the vendor's "pokemon". Radiant Charizard from Pokemon GO failed on
    // exactly that: the right card was in the results and the set comparison
    // threw it out over one character.
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s*\(.*\)/, '')
    .replace(eras ? new RegExp(`\\b(${eras})\\d+\\b`, 'g') : /$^/, '$1')
    .split(/[^a-z0-9&]+/)
    .filter(Boolean);
};

/**
 * Is every meaningful word of `ours` present in `theirs`, with only filler left
 * over? "Scarlet & Violet" is inside "SV01: Scarlet & Violet Base Set" and
 * matches; "Team Rocket" is inside "Team Rocket Returns" and does not, because
 * "returns" is a real word.
 */
/** "me02", "swsh03", "sv01" - a set id sitting in the set name field. */
const looksLikeSetId = (name) => /^[a-z]{2,4}\d+(\.\d+[a-z]?)?$/i.test(String(name || '').trim());

function setIsSubsetOf(ours, theirs) {
  // Their name is prefixed with the id, so "ME02: Phantasmal Flames" answers a
  // set name of "me02" exactly and unambiguously.
  if (looksLikeSetId(ours)) {
    return String(theirs || '').toLowerCase().replace(/[^a-z0-9.]/g, ' ').trim()
      .startsWith(String(ours).toLowerCase());
  }
  const a = setWords(ours);
  const b = new Set(setWords(theirs));
  if (!a.length || !b.size) return false;
  if (!a.every((w) => b.has(w))) return false;
  const filler = setFiller();
  const extras = [...b].filter((w) => !a.includes(w));
  return extras.every((w) => filler.has(w));
}

/**
 * Would the matcher's set gate accept this candidate's set name for ours?
 *
 * Same four rules, in the same order, so the price cache cannot hand back a row
 * the matcher would have rejected. Nothing to compare on is not a mismatch: a
 * cached row with no set recorded is left to the finish and stamp gates.
 */
export function setNamesAgree(ourSetName, theirSetName) {
  const target = String(ourSetName || '').toLowerCase();
  const cand = String(theirSetName || '').toLowerCase();
  if (!target || !cand) return true;
  const base = cand.replace(/\s*\(.*\)/, '').trim();
  return base === target || cand === target
    || base.endsWith(target) || target.includes(base)
    || setIsSubsetOf(target, cand);
}

async function searchPoketraceCardOnce(cardName, cardNumber, setName, variant, searchOffset = 0) {
  const perfLabel = `[Perf] searchPoketraceCard: ${cardName}`;
  console.time(perfLabel);
  if (!VENDOR_READY) {
    console.warn('[Poketrace] API key not configured');
    console.timeEnd(perfLabel);
    return { card: null, failure: { stage: FAILURE_STAGES.NO_API_KEY, diagnostics: {} } };
  }

  // A verified alias replaces our set name with the vendor's before anything
  // else happens. Without it the query itself is wrong, and no amount of
  // scoring downstream can recover a card that was never in the results.
  // Fire and forget past the first call: the TTL keeps this a no-op, and a
  // failed fetch leaves the compiled defaults in place.
  await loadMatchConfig().catch(() => {});
  const alias = await resolveSetAlias(setName, cardNumber, 'poketrace');
  const effectiveSetName = alias?.vendorSetName || setName;

  // !! DO NOT CHANGE search query construction without asking Chase first !!
  // Name + set + number keeps Poketrace results specific. Without the number,
  // common names like "Lugia" return wrong cards (e.g. Neo Genesis instead of Skyridge).
  // Poketrace caps results at 20, so specificity matters.
  let searchQuery = cardName;
  if (alias?.searchHint) {
    searchQuery += ` ${alias.searchHint}`;
  } else if (effectiveSetName) {
    searchQuery += ` ${effectiveSetName}`;
  }
  if (cardNumber) searchQuery += ` ${cardNumber.split('/')[0]}`;

  // The vendor caps a page at 20 and honours `offset`. We never asked for a
  // second page, so a card listed past the first was invisible however good the
  // query was: "Drapion 4" is filed as "Drapion E4" and sits at offset 40. The
  // caller walks the offsets only after a miss, so the extra calls land on
  // failures rather than on every card.
  const searchPath = searchOffset > 0
    ? `${API_BASE}/cards?search=${encodeURIComponent(searchQuery)}&offset=${searchOffset}`
    : `${API_BASE}/cards?search=${encodeURIComponent(searchQuery)}`;

  const res = await ptGet(searchPath);

  if (!res.ok) {
    console.error('[Poketrace] Search failed:', res.status);
    console.timeEnd(perfLabel);
    return {
      card: null,
      failure: { stage: FAILURE_STAGES.SEARCH_ERROR, diagnostics: { searchQuery, httpStatus: res.status } },
    };
  }

  const data = await res.json();
  const cards = data.data || data.cards || data || [];

  if (!Array.isArray(cards) || cards.length === 0) {
    console.timeEnd(perfLabel);
    return {
      card: null,
      failure: { stage: FAILURE_STAGES.SEARCH_EMPTY, diagnostics: { searchQuery, candidateCount: 0 } },
    };
  }

  // Prefer US market cards over EU.
  //
  // This reads as a preference and behaves as a hard filter: any US card in the
  // results discards every EU one, including exact matches. On promo sets that
  // is fatal, because the EU rows are often the only ones carrying our set name.
  // The alias above is what makes that survivable — it points the search at the
  // vendor's US catalog so the right rows are in the pool to begin with. When a
  // card still dies here, the dropped EU matches are recorded so the diagnosis
  // can see what the filter took.
  const usCards = cards.filter(c => c.market === 'US');
  const searchPool = usCards.length > 0 ? usCards : cards;
  const droppedExactSetMatches = usCards.length > 0
    ? cards
        .filter(c => c.market !== 'US'
          && (c.set?.name || '').toLowerCase() === (effectiveSetName || '').toLowerCase())
        .map(c => ({ id: c.id, cardNumber: c.cardNumber, set: c.set?.name, variant: c.variant, market: c.market }))
    : [];

  const normalizedNumber = normalizeCardNumber(cardNumber);
  const variantLower = (variant || '').toLowerCase();
  const is1stOrShadowless = variantLower.includes('1st') || variantLower.includes('first') || variantLower.includes('shadowless');
  // The stamp the owner's card carries, independent of its foiling.
  const wantStamp = canonicalStamp(variant);
  // And which print run, when our record actually says.
  const wantRun = canonicalPrintRun(variant);

  // Score candidates: higher is better match
  const scoredCandidates = [];

  // Which gate each rejected candidate died at. The deepest gate reached is the
  // most informative thing we can tell the repair loop: if candidates cleared
  // the number check and died on the set name, the set name is the problem.
  const rejected = { name: 0, number: 0, set: 0, finish: 0, stamp: 0 };

  // Log all candidates so we can see what Poketrace returned
  console.log(`[Poketrace] Search returned ${searchPool.length} candidates for "${searchQuery}":`);
  searchPool.forEach((c, i) => {
    console.log(`  [${i}] #${c.cardNumber} | set: ${c.set?.name} | variant: ${c.variant} | market: ${c.market}`);
  });

  const finishOnlyRejects = [];
  // "54a", "107a" - a printing the vendor catalogues away from its parent set.
  const suffixedNumber = /\d+[a-z]$/i.test(String(normalizedNumber || '').split('/')[0]);

  for (const card of searchPool) {
    const candidateNumber = normalizeCardNumber(card.cardNumber);
    const candidateSet = (card.set?.name || '').toLowerCase();
    const candidateVariant = (card.variant || '').toLowerCase();
    const targetSet = (effectiveSetName || '').toLowerCase();
    // Read before the set gate, because for a stamped printing the stamp is what
    // decides whether the set name is even evidence. See the gate below.
    const gotStamp = vendorStamp(card);
    const gotRun = canonicalPrintRun(card.variant, card.set?.name, card.name);

    let score = 0;

    // Is this even the card we asked for?
    //
    // Nothing checked. isSameCard existed but only ran inside the tie-break, so a
    // single candidate that cleared the number and the set won on its own with a
    // completely different name on it: Rocket's Hitmonchan was priced off Rocket's
    // Scizor, Manaphy off Machoke, Professor's Research (Magnolia) off the Willow
    // printing, and several cards off sealed theme-deck listings. It is also what
    // blocks a Celebrations Classic Collection alias, where the vendor numbers
    // every card by its original print so our CC002 Charizard would have matched
    // the vendor's 2/102 Blastoise.
    //
    // First gate, before the number, because a candidate that is not this card
    // says nothing about our numbering.
    if (cardName && !isSameCard(card.name, cardName)) {
      rejected.name += 1;
      continue;
    }

    // Card number match - handle "4" vs "4/102" (TCGdex localId vs Poketrace full number)
    if (normalizedNumber && candidateNumber === normalizedNumber) {
      score += 10;
    } else if (normalizedNumber && candidateNumber.split('/')[0] === normalizedNumber.split('/')[0]) {
      score += 10; // Match on card number portion (e.g. "4" matches "4/102")
    } else if (normalizedNumber && bareNumber(candidateNumber) === bareNumber(normalizedNumber)
      && hasNumberPrefix(candidateNumber) !== hasNumberPrefix(normalizedNumber)) {
      // Promo numbering: the catalogue says "BW04", the vendor says "04" inside
      // a set that already carries the BW. Same card. Only when one side has
      // the letters and the other has none; "BW04" never matches "XY04".
      score += 9;
    } else if (normalizedNumber) {
      rejected.number += 1;
      continue; // Skip if we have a number and it doesn't match
    }

    // Set name match - handle Poketrace prefixed names like "SM - Ultra Prism",
    // "SWSH03: Darkness Ablaze", "SV01: Scarlet & Violet Base Set", while still
    // avoiding "Team Rocket" matching "Team Rocket Returns".
    if (targetSet && candidateSet) {
      const candidateBase = candidateSet.replace(/\s*\(.*\)/, '').trim();
      // "SV01: Scarlet & Violet Base Set" against our "Scarlet & Violet" was the
      // single biggest cause of a card showing no price: endsWith() fails
      // because theirs ends in "base set", and includes() fails because theirs
      // is the longer string. Every word of ours is in theirs and the leftovers
      // are filler, which is the thing actually worth testing.
      if (candidateBase === targetSet || candidateSet === targetSet) {
        score += 5;
      } else if (candidateBase.endsWith(targetSet) || targetSet.includes(candidateBase)) {
        score += 3;
      } else if (setIsSubsetOf(targetSet, candidateSet)) {
        score += 2;
      } else if (wantStamp && gotStamp === wantStamp && matchConfig().stampOverridesSet) {
        // A stamped printing is often filed away from its parent set: the
        // prerelease Dark Gyarados of Team Rocket 8 lives in "WoTC Promo", so
        // insisting the set names agree threw away the only listing that has the
        // stamp at all. Number plus stamp is close to unique, and both have
        // already agreed by the time this runs, so the set name is not evidence
        // here. Scored lowest of the branches, so a candidate that does match the
        // set still wins.
        score += 1;
      } else if (suffixedNumber && matchConfig().suffixedNumberOverridesSet) {
        // A letter-suffixed number is close to unique, and the vendor files
        // those printings under promo sets rather than the parent set. Fates
        // Collide Zygarde EX 54a lives in "Alternate Art Promos" there, so
        // demanding the set names agree threw away the only real match. The
        // number carries the identity here; the set name is not evidence.
        score += 1;
      } else {
        rejected.set += 1;
        continue; // Skip if set doesn't match at all
      }
    }

    // Variant matching: 1st Edition / Shadowless
    const candidate1stOrShadowless = candidateSet.includes('shadowless') ||
      candidateVariant.includes('1st') ||
      candidateVariant.includes('first') ||
      candidateVariant.includes('shadowless');

    // A stamp is part of which card this is, not a note about it.
    //
    // Checked before the finish, and it rejects rather than penalises. A
    // prerelease Dark Gyarados and an ordinary one are different cards selling
    // at very different numbers, so an unstamped entry is not a near miss for a
    // stamped card - it is the wrong card, and pricing it produced $121 for one
    // worth thousands. Where the vendor has no stamped entry the card ends with
    // no price and a logged failure, which is the honest answer and one the
    // repair loop can see.
    if (wantStamp) {
      if (gotStamp === wantStamp) score += 25;
      else { rejected.stamp += 1; continue; }
    } else if (wantRun === 'unlimited' && (gotRun === 'first' || gotRun === 'shadowless')) {
      // Our record says unlimited, which is a positive statement and not a blank:
      // this card is neither 1st edition nor shadowless. Base Set Charizard read
      // $3,092.23 on the unlimited printing, to the cent the same as the
      // shadowless one, because the two differ only by a -10 here and the
      // shadowless row won whenever the plain row was not in the pool. A run we
      // were told this card is not is the wrong card, not a near miss.
      rejected.stamp += 1;
      continue;
    } else if (gotStamp && gotStamp !== 'first' && gotStamp !== 'shadowless') {
      // The owner's card carries no stamp, so a stamped entry is the wrong one.
      // 1st edition and shadowless keep their older, gentler handling below,
      // which the vintage sets depend on.
      rejected.stamp += 1;
      continue;
    }

    if (is1stOrShadowless) {
      // User has 1st Edition / Shadowless -- prefer matching entries
      if (candidate1stOrShadowless) {
        score += 20; // Strong preference for 1st ed / shadowless entry
      } else {
        score -= 10; // Penalize unlimited when user has 1st ed
      }
    } else if (variant) {
      // Finish must match exactly. Poketrace lists Normal, Reverse_Holofoil and
      // Holofoil as separate priced entries for the same card, so a near miss
      // here returns a real price for the wrong card.
      const wantFinish = canonicalFinish(variant);
      const gotFinish = canonicalFinish(card.variant);
      if (wantFinish && gotFinish) {
        if (wantFinish === gotFinish) {
          score += 20;
        } else {
          rejected.finish += 1;
          // Kept aside rather than discarded. Poketrace lists each finish as
          // its own priced entry, so when a number has two of them a finish
          // mismatch really is a different card. When it has only one, the
          // disagreement is our own record being wrong - a Special Illustration
          // Rare recorded as Normal is still that card, and refusing to price
          // it was the second most common reason a card showed nothing.
          finishOnlyRejects.push({ card, score: score + 5 });
          continue;
        }
      }
      // Penalize 1st ed / shadowless entry when user does NOT have 1st ed
      if (candidate1stOrShadowless) {
        score -= 10;
      }
    } else {
      // No variant specified - avoid shadowless/1st ed unless set name explicitly says it
      if (candidate1stOrShadowless && !targetSet.includes('shadowless')) {
        score -= 10;
      }
      // Nothing told us which printing this is, so prefer the base card. The
      // vendor lists HeartGold SoulSilver Fisherman 92 as Normal and as
      // Reverse_Holofoil, both named "Fisherman", and with no finish to score
      // they tied at 15 and the card was refused as ambiguous - the one outcome
      // that helps nobody, since a reverse is the premium printing and a caller
      // who did not name one is holding the ordinary card.
      const gotFinish = canonicalFinish(card.variant);
      if (gotFinish === 'reverse') score -= 2;
      else if (gotFinish === 'holo') score -= 1;
    }

    // Oversized promos share the number and are a different product entirely,
    // so they must never win a tie against the real card. Which patterns count
    // is a row, not a constant - the next one like Jumbo needs no release.
    for (const rule of demoteRules()) {
      if (rule.re.test(candidateSet)) score -= rule.penalty;
    }

    if (score > 0) {
      scoredCandidates.push({ card, score });
    }
  }

  // Only when the number and set left exactly one printing standing, and
  // nothing else survived. Two finishes for one number stay rejected.
  //
  // Never across the reverse holo line, in either direction. The holo-for-normal
  // rule below has always refused to swap a reverse in, and this rule did not:
  // asked for the holo Drapion 4 with only the vendor's Reverse_Holofoil in the
  // pool, it handed back the reverse's number. 443 cards carry a reverse and a
  // non-reverse printing priced identically to the cent, which is what that looks
  // like from the outside. A reverse is a different printing at a different price.
  if (matchConfig().singlePrintingBeatsFinish
      && scoredCandidates.length === 0 && finishOnlyRejects.length === 1) {
    const only = finishOnlyRejects[0];
    const wantFinish = canonicalFinish(variant);
    const gotFinish = canonicalFinish(only.card.variant);
    if ((wantFinish === 'reverse') === (gotFinish === 'reverse')) {
      console.log('[Poketrace] Accepting the only printing despite a finish mismatch');
      scoredCandidates.push(only);
    } else {
      console.log('[Poketrace] Refusing the only printing: it is across the reverse line');
    }
  }

  // A Rare Holo our catalogue records as Normal.
  //
  // tcgdex gives these rarity "Rare" and a single variant of type normal, so we
  // go looking for a Normal printing that was never made. The vendor's US rows
  // are Holofoil and Reverse_Holofoil; the only Normal is the EU one the market
  // filter above already dropped. The rule just before this one cannot help,
  // because it needs exactly one surviving printing and here there are two.
  //
  // Holofoil only, never the reverse. A reverse holo is a different printing at
  // a different price, and handing one over as the other is a confident wrong
  // answer, which is worse than showing nothing. Mirrors the same rule in
  // supabase/functions/_shared/poketrace.ts.
  if (scoredCandidates.length === 0 && canonicalFinish(variant) === 'normal') {
    const holos = finishOnlyRejects.filter((c) => canonicalFinish(c.card.variant) === 'holo');
    if (holos.length === 1) {
      console.log('[Poketrace] Rare Holo recorded as Normal, taking the Holofoil:', holos[0].card.name);
      scoredCandidates.push(holos[0]);
    }
  }

  // Sort by score descending
  scoredCandidates.sort((a, b) => b.score - a.score);

  if (scoredCandidates.length > 0) {
    console.log(`[Poketrace] Scored candidates (user variant: "${variant || 'none'}"):`);
    scoredCandidates.forEach((c, i) => {
      console.log(`  [${i}] score: ${c.score} | ${c.card.set?.name} | variant: ${c.card.variant} | #${c.card.cardNumber}`);
    });
    // A tie is not a match. Two rows can share a number and a finish and differ
    // only by a suffix in the name - the Pokemon Center printing of a promo runs
    // several times the price of the standard one. Picking the first is a coin
    // flip on which price the user sees, and a wrong price is worse than none.
    const topScore = scoredCandidates[0].score;
    const tied = scoredCandidates.filter((c) => c.score === topScore);

    let tieBroken = null;
    let winner = scoredCandidates[0];

    if (tied.length > 1) {
      // Only printings of the card we actually asked for may win. Everything
      // else in the tie is a different Pokemon sharing a number inside a merged
      // set, and no amount of name scoring makes one of those the right answer.
      const ourPrintings = tied.filter((c) => isSameCard(c.card.name, cardName));

      // Try the plain name before giving up on the card entirely.
      const ranked = [...ourPrintings].sort(
        (a, b) => nameSpecificity(a.card.name, cardName) - nameSpecificity(b.card.name, cardName),
      );
      const best = ranked.length ? nameSpecificity(ranked[0].card.name, cardName) : null;
      const alsoBest = ranked.filter((c) => nameSpecificity(c.card.name, cardName) === best);

      if (ourPrintings.length === 1 || alsoBest.length === 1) {
        winner = ranked[0];
        tieBroken = {
          chosen: winner.card.name,
          set: winner.card.set?.name || null,
          variant: winner.card.variant || null,
          rejected: tied.filter((c) => c.card.id !== winner.card.id).map((c) => c.card.name),
        };
        console.log('[Poketrace] Tie broken on name:', tieBroken.chosen, 'over', tieBroken.rejected.join(', '));
      }
    }

    if (tied.length > 1 && !tieBroken) {
      console.warn('[Poketrace] Ambiguous match, refusing to guess:', tied.map((t) => t.card.name).join(' / '));
      console.timeEnd(perfLabel);
      return {
        card: null,
        failure: {
          stage: FAILURE_STAGES.AMBIGUOUS_MATCH,
          diagnostics: {
            searchQuery,
            ourSetName: setName,
            effectiveSetName,
            aliasApplied: Boolean(alias),
            wantFinish: canonicalFinish(variant) || null,
            tiedAt: topScore,
            tied: tied.map((t) => ({
              id: t.card.id,
              name: t.card.name,
              cardNumber: t.card.cardNumber,
              set: t.card.set?.name,
              variant: t.card.variant,
            })),
          },
        },
      };
    }

    console.log('[Poketrace] Best match:', winner.card.set?.name, winner.card.variant, 'score:', winner.score);
    console.timeEnd(perfLabel);
    return { card: winner.card, failure: null, tieBroken };
  }

  // There used to be a fallback here that returned the first candidate from a
  // set whose name matched, ignoring the number and the finish entirely. It is
  // gone, and _shared/poketrace.ts dropped it on 09.11.2026 for the same reason:
  // every row it could reach had already been rejected by a gate, so what it
  // handed back was a different printing, or a different card, wearing the right
  // set name. It is what priced a "Normal" Base Set Charizard - a printing that
  // was never made - off the holo listing, at a number above the holo row
  // itself. A card we cannot match is a card we do not price.

  // !! DO NOT add a fallback here that returns searchPool[0] or any unmatched card !!
  // Returning a wrong card produces wrong prices. Null is correct - tile shows "?".
  console.warn('[Poketrace] No matching card found for:', searchQuery);
  console.timeEnd(perfLabel);

  // Name the deepest gate anything reached. A candidate that cleared the number
  // check and died on the set name says something quite different from nothing
  // matching the number at all, and the fix differs accordingly.
  // Only when the name search itself came back empty. A card whose number never
  // matched anything is a different problem from one the vendor does not list.
  const stage = (cards.length === 0) ? FAILURE_STAGES.NOT_CATALOGUED
    : rejected.stamp > 0 ? FAILURE_STAGES.NO_STAMP_MATCH
    : rejected.finish > 0 ? FAILURE_STAGES.NO_FINISH_MATCH
    : rejected.set > 0 ? FAILURE_STAGES.NO_SET_MATCH
    : rejected.number > 0 ? FAILURE_STAGES.NO_NUMBER_MATCH
    // Everything the vendor sent back was a different card. The search terms are
    // the thing to look at, not the printing.
    : rejected.name > 0 ? FAILURE_STAGES.NO_NAME_MATCH
    : FAILURE_STAGES.NO_NUMBER_MATCH;

  return {
    card: null,
    failure: {
      stage,
      diagnostics: {
        searchQuery,
        ourSetName: setName,
        effectiveSetName,
        aliasApplied: Boolean(alias),
        wantFinish: canonicalFinish(variant) || null,
        wantStamp: canonicalStamp(variant) || null,
        candidateCount: cards.length,
        poolCount: searchPool.length,
        rejected,
        droppedExactSetMatches,
        // Capped: enough for a diagnosis, not enough to bloat the row.
        candidates: searchPool.slice(0, 20).map(c => ({
          id: c.id,
          cardNumber: c.cardNumber,
          set: c.set?.name,
          variant: c.variant,
          market: c.market,
          name: c.name,
        })),
      },
    },
  };
}

const DELTA = 'δ';

/** "Arbok δ" -> "Arbok". */
const withoutDelta = (name) => String(name || '')
  .replace(/\s*δ\s*/g, ' ')
  .replace(/\s{2,}/g, ' ')
  .trim();

/**
 * One retry for Delta Species cards.
 *
 * Our catalogue keeps the delta symbol in the card's name, "Arbok δ". The
 * vendor's US listing for the same card is "Arbok (Delta Species)" with no
 * symbol, and searching with the symbol steers the results to the vendor's EU
 * rows. The US filter then discards those, so the card dies at the set gate
 * having never had its real listing in the pool at all.
 *
 * A retry rather than always stripping it. 191 cards in the catalogue carry the
 * symbol and 126 of them already price correctly, so changing the query for
 * every one of them to recover the other 65 would put working cards at risk for
 * nothing. A card that already matches returns on the first attempt and never
 * reaches this.
 *
 * The first attempt's failure is what gets returned when the retry also fails,
 * so the repair loop still sees the original diagnosis rather than a second one
 * about a name the catalogue does not actually hold.
 */
async function searchPoketraceCardDetailed(cardName, cardNumber, setName, variant) {
  const first = await searchPoketraceCardOnce(cardName, cardNumber, setName, variant);
  if (first.card) return first;

  if (String(cardName || '').includes(DELTA)) {
    const plain = withoutDelta(cardName);
    if (plain && plain !== cardName) {
      const second = await searchPoketraceCardOnce(plain, cardNumber, setName, variant);
      if (second.card) return second;
    }
  }

  // Walk two more pages of vendor results. Only on a miss, and only two, so one
  // lookup never turns into a scan of the catalogue.
  for (const offset of [20, 40]) {
    const paged = await searchPoketraceCardOnce(cardName, cardNumber, setName, variant, offset);
    if (paged.card) return paged;
  }

  // Last resort: find it by set and number, for the vendor rows whose name
  // field holds the set name.
  const byNumber = await searchPoketraceBySetAndNumber(cardNumber, setName, variant);
  if (byNumber) return { card: byNumber, failure: null };

  // A tie the vendor created by listing one printing twice.
  //
  // Base Set Alakazam 1 is filed both as "Base Set / 1st_Edition_Holofoil" and as
  // "Base Set (Shadowless) / 1st_Edition_Holofoil". Every Base Set 1st edition IS
  // shadowless, so those are one card under two entries and no gate can separate
  // them: both clear the number, the set, the stamp and the finish, and the card
  // was refused as ambiguous.
  //
  // The set label is not the tell. Alakazam's sales are on the "Base Set" entry
  // (386, 42 priced tiers) and its shadowless twin holds 10 sales and no priced
  // tier at all, while Charizard's are the other way round. So ask the vendor
  // which of the tied entries it can actually price, and take it only when
  // exactly one answers. Two calls, and only on a tie.
  const tied = first.failure?.stage === FAILURE_STAGES.AMBIGUOUS_MATCH
    ? (first.failure.diagnostics?.tied || [])
    : [];
  if (tied.length >= 2 && tied.length <= 3) {
    const fetched = [];
    for (const candidate of tied) {
      const data = await fetchPoketracePricing(candidate.id);
      if (data) fetched.push({ candidate, data });
    }
    // Sold data, specifically. The empty twin of Alakazam carries four TCGplayer
    // ask tiers and not one eBay sale, so "has any prices at all" calls both
    // entries priced and refuses again. eBay is what the headline is built from.
    const sold = fetched.filter((f) => Object.keys(f.data?.prices?.ebay || {}).length > 0);
    const anyMarket = fetched.filter((f) =>
      Object.values(f.data?.prices || {}).some((m) => m && Object.keys(m).length > 0));
    const priced = sold.length === 1 ? sold : (sold.length === 0 ? anyMarket : []);
    if (priced.length === 1) {
      console.log('[Poketrace] Tie broken on which entry the vendor prices:', priced[0].candidate.id);
      // Hand back the vendor's own card object, not the diagnostics summary: the
      // summary flattens `set` to a string and callers read `card.set.name`.
      return { card: priced[0].data, failure: null };
    }
  }

  return first;
}

/**
 * Find a card the vendor filed under a broken name, using set plus number.
 *
 * A batch of the vendor's US rows have the SET name in the card NAME field: the
 * row for Undaunted 31/90 is literally called "Undaunted". Searching for "Muk"
 * cannot reach it at any page depth, and the EU row that does carry the name is
 * dropped by the US filter. Seen the same way in Undaunted, EX Legend Maker,
 * Secret Wonders, Platinum and Crown Zenith.
 *
 * Set plus number identifies a card uniquely, so this is safe, but only under
 * the exact signature of the defect: the candidate's name equals its own set's
 * name, the number matches, the set is the one we asked for, and exactly one
 * row qualifies. Mirrors matchBySetAndNumber in _shared/poketrace.ts.
 */
async function searchPoketraceBySetAndNumber(cardNumber, setName, variant) {
  if (!VENDOR_READY || !cardNumber || !setName) return null;

  const alias = await resolveSetAlias(setName, cardNumber, 'poketrace').catch(() => null);
  const effectiveSetName = alias?.vendorSetName || setName;
  const query = `${effectiveSetName} ${String(cardNumber).split('/')[0]}`;

  let cards = [];
  try {
    const res = await ptGet(`/cards?search=${encodeURIComponent(query)}`);
    if (!res.ok) return null;
    const data = await res.json();
    cards = data.data || data.cards || data || [];
    if (!Array.isArray(cards)) return null;
  } catch {
    return null;
  }

  const norm = (n) => (n || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const wantNumber = normalizeCardNumber(cardNumber);
  const wantSet = norm(effectiveSetName);

  const hits = cards.filter((c) => {
    if (c.market !== 'US') return false;

    const candSet = norm(c.set?.name);
    if (!candSet || norm(c.name) !== candSet) return false;
    if (candSet !== wantSet && !candSet.endsWith(wantSet) && !wantSet.endsWith(candSet)) return false;

    const n = normalizeCardNumber(c.cardNumber);
    if (n !== wantNumber && n.split('/')[0] !== wantNumber.split('/')[0]) return false;

    const want = canonicalFinish(variant);
    const got = canonicalFinish(c.variant);
    if (want && got && want !== got && !(want === 'normal' && got === 'holo')) return false;

    return true;
  });

  if (hits.length !== 1) return null;
  console.log('[Poketrace] Matched by set and number past a broken vendor name:', hits[0].id);
  return hits[0];
}

/**
 * Back-compatible wrapper. Callers that only need the match keep working.
 */
async function searchPoketraceCard(cardName, cardNumber, setName, variant) {
  const { card } = await searchPoketraceCardDetailed(cardName, cardNumber, setName, variant);
  return card;
}

/**
 * Fetch full pricing data for a Poketrace card ID
 */
async function fetchPoketracePricing(poketraceId) {
  const perfLabel = `[Perf] fetchPoketracePricing: ${poketraceId}`;
  console.time(perfLabel);
  if (!VENDOR_READY) { console.timeEnd(perfLabel); return null; }

  const res = await ptGet(`/cards/${poketraceId}`);

  if (!res.ok) {
    console.error('[Poketrace] Pricing fetch failed:', res.status);
    console.timeEnd(perfLabel);
    return null;
  }

  const json = await res.json();
  console.timeEnd(perfLabel);
  return json.data || json;
}

/**
 * Fetch individual sold listings for a Poketrace card ID.
 * Paginates through all pages (cursor-based), handles rate limiting.
 * Caps at 10 pages (200 listings) to avoid excessive API calls.
 * Filters to US eBay data via ?country=us.
 *
 * Response shape:
 * {
 *   "data": [...listings],
 *   "pagination": { "hasMore": true, "nextCursor": "MjA=", "count": 20 },
 *   "rateLimit": { "remaining": 97, "max": 100 }
 * }
 */
export async function fetchPoketraceListings(poketraceId) {
  const perfLabel = `[Perf] fetchPoketraceListings: ${poketraceId}`;
  console.time(perfLabel);
  if (!VENDOR_READY || !poketraceId) { console.timeEnd(perfLabel); return []; }

  const allListings = [];
  let cursor = null;
  let pageCount = 0;
  let rateLimitRetries = 0;
  const MAX_PAGES = 10;
  const MAX_RATE_LIMIT_RETRIES = 3;

  while (pageCount < MAX_PAGES) {
    const params = new URLSearchParams({ country: 'us' });
    if (cursor) params.set('cursor', cursor);
    const url = `${API_BASE}/cards/${poketraceId}/listings?${params.toString()}`;

    let res;
    try {
      res = await ptGet(url);
    } catch (err) {
      console.error('[Poketrace] Listings fetch network error:', err);
      break;
    }

    if (!res.ok) {
      // Handle rate limiting with retry cap
      if (res.status === 429 && rateLimitRetries < MAX_RATE_LIMIT_RETRIES) {
        rateLimitRetries++;
        const waitTime = 4;
        console.warn(`[Poketrace] Rate limited (retry ${rateLimitRetries}/${MAX_RATE_LIMIT_RETRIES}), waiting ${waitTime}s...`);
        await new Promise(resolve => setTimeout(resolve, waitTime * 1000));
        continue; // Retry same page
      }
      if (res.status === 429) {
        console.warn(`[Poketrace] Rate limit retries exhausted, returning ${allListings.length} listings collected so far`);
        break;
      }
      console.error('[Poketrace] Listings fetch failed:', res.status);
      break;
    }

    // Reset retry counter on success
    rateLimitRetries = 0;

    let json;
    try {
      json = await res.json();
    } catch {
      console.error('[Poketrace] Listings JSON parse error');
      break;
    }

    const listings = json.data || [];
    if (listings.length === 0) break;

    allListings.push(...listings);
    pageCount++;

    const pagination = json.pagination || {};
    const rateLimit = json.rateLimit || {};
    console.log(`[Poketrace] Listings page ${pageCount}: ${listings.length} items (total: ${allListings.length}, remaining: ${rateLimit.remaining ?? '?'})`);

    if (!pagination.hasMore) break;
    cursor = pagination.nextCursor;
    if (!cursor) break;

    // Brief pause between pages to be respectful of rate limits
    if (rateLimit.remaining != null && rateLimit.remaining < 5) {
      console.warn('[Poketrace] Rate limit low, pausing 3s...');
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
  }

  console.log(`[Poketrace] Total listings fetched: ${allListings.length} across ${pageCount} pages`);
  console.timeEnd(perfLabel);
  return allListings;
}

// ---- Supabase Cache Layer ----

/**
 * Look up cached pricing data from Supabase by card name + number + finish.
 *
 * Name and number alone are not a unique key. Poketrace stores Normal and
 * Reverse_Holofoil as separate entries under the SAME card number, so the old
 * single-row lookup returned whichever finish happened to be cached first and
 * served it for every finish of that card.
 */
async function getCachedPrice(cardName, cardNumber, variant, setName = null) {
  // Match on the name in the query and the number in JS.
  //
  // The number was an exact column match, and the two sides never agreed: rows
  // are written under the vendor's format ("004/102") while lookups arrive in
  // ours ("4"). 190 cached rows were serving 6 of 205 cards, so nearly every
  // card open paid a full round trip to re-fetch something already stored.
  // Comparing only the part before the slash is what the matcher itself does.
  const { data: rows, error } = await supabase
    .from('poketrace_price_cache')
    .select('*')
    .eq('card_name', cardName);

  const wantNumber = normalizeCardNumber(cardNumber).split('/')[0];
  const numbered = (rows || []).filter((r) => {
    if (!wantNumber) return true;
    const got = normalizeCardNumber(r.card_number).split('/')[0];
    return got === wantNumber;
  });

  // The set has to agree too.
  //
  // It was never checked, and the cache is keyed on the card name and the number
  // before the slash, so a cached "Base Set 2" Charizard at 004/130 answered
  // every request for Base Set 004/102: the plain holo row read $335.37, which is
  // Base Set 2's number, off 150 sales whose titles say Base Set 2. Compared
  // through the alias, because the row stores the vendor's set name and the
  // request carries ours.
  const alias = setName ? await resolveSetAlias(setName, cardNumber, 'poketrace').catch(() => null) : null;
  const effectiveSetName = alias?.vendorSetName || setName;
  const ourSet = String(setName || '').trim().toLowerCase();
  const inSet = (effectiveSetName ? numbered : []).filter((r) => {
    // Written by every save since 09.13.2026, and the exact answer: this row was
    // fetched for this set of ours.
    if (r.our_set_name) return String(r.our_set_name).trim().toLowerCase() === ourSet;
    // Older rows only know the vendor's set name, so they are compared the way
    // the matcher's set gate compares it.
    const d = r.price_data?.data || r.price_data;
    return setNamesAgree(effectiveSetName, d?.set?.name || r.set_name);
  });
  const pool = effectiveSetName ? inSet : numbered;

  let data = null;
  if (pool.length) {
    // The stamp narrows the pool before the finish does, for the same reason it
    // does in the matcher: a cached unstamped row is not a near miss for a
    // stamped card, it is a different card at a different price. Without this
    // the gate above is undone on the very next visit, because the wrong row is
    // already sitting in the cache.
    //
    // Read through vendorStamp, not the variant alone. The shadowless Charizard
    // is variant "Unlimited_Holofoil" in set "Base Set (Shadowless)", so the
    // variant on its own says no stamp at all: it passed this filter for an
    // unlimited card, matched on finish "holo", and the unlimited printing was
    // served the shadowless row's $3,092.23, to the cent, from cache. The
    // matcher gate was right and this undid it on the next read.
    const wantStamp = canonicalStamp(variant);
    const stamped = pool.filter((r) => {
      const d = r.price_data?.data || r.price_data;
      return (vendorStamp(d) || null) === (wantStamp || null);
    });
    if (wantStamp && stamped.length === 0) return null;

    // And the print run, which the stamp filter above cannot see when our own
    // label carries it as a subtype rather than a stamp ("Holo / Unlimited").
    const wantRun = canonicalPrintRun(variant);
    const runFiltered = (stamped.length ? stamped : pool).filter((r) => {
      const d = r.price_data?.data || r.price_data;
      const gotRun = canonicalPrintRun(d?.variant, d?.set?.name || r.set_name, d?.name);
      if (wantRun === 'unlimited') return gotRun !== 'first' && gotRun !== 'shadowless';
      return true;
    });
    if (wantRun === 'unlimited' && runFiltered.length === 0) return null;

    const rowsForFinish = runFiltered.length ? runFiltered : (stamped.length ? stamped : pool);
    const want = canonicalFinish(variant);
    if (want) {
      data = rowsForFinish.find((r) => {
        const d = r.price_data?.data || r.price_data;
        return canonicalFinish(d?.variant) === want;
      }) || null;
    } else if (rowsForFinish.length === 1) {
      // No finish asked for and only one entry exists - safe to use.
      data = rowsForFinish[0];
    }
    // Several finishes cached and no finish requested: fall through to a fresh
    // lookup rather than guess. Guessing is what produced the wrong prices.
  }

  // Expired rows are treated as a miss so the caller re-fetches.
  if (data && isCacheStale(data.fetched_at, data.set_name)) {
    return null;
  }

  if (error) {
    console.error('[Poketrace] Cache lookup error:', error);
    return null;
  }
  return data;
}

/**
 * Look up cached pricing by poketrace_id
 */
async function getCachedPriceById(poketraceId) {
  const { data, error } = await supabase
    .from('poketrace_price_cache')
    .select('*')
    .eq('poketrace_id', poketraceId)
    .maybeSingle();

  if (error) {
    console.error('[Poketrace] Cache lookup error:', error);
    return null;
  }
  return data;
}

/**
 * Bulk fetch cached prices for multiple cards in a single query.
 * Returns a Map keyed by "cardName||cardNumber" with formatted price results.
 */
export async function getBulkCachedPrices(cards) {
  if (!cards || cards.length === 0) return new Map();

  const cardNames = [...new Set(cards.map(c => c.cardName))];

  const { data, error } = await supabase
    .from('poketrace_price_cache')
    .select('*')
    .in('card_name', cardNames);

  if (error) {
    console.error('[Poketrace] Bulk cache lookup error:', error);
    return new Map();
  }

  // Index cached rows by card_name + card_number
  const cacheMap = new Map();
  for (const row of (data || [])) {
    const key = `${row.card_name}||${row.card_number || ''}`;
    cacheMap.set(key, row);
  }

  // Build results
  const results = new Map();
  for (const card of cards) {
    const key = `${card.cardName}||${card.cardNumber || ''}`;
    const cached = cacheMap.get(key);
    if (cached) {
      const result = formatPriceResult(cached.price_data, card.grade, cached.fetched_at);
      if (result?.price) {
        results.set(card.id, result);
      }
    }
  }

  return results;
}

/**
 * Save pricing data to Supabase cache
 */
async function savePriceToCache(poketraceId, cardName, setName, cardNumber, priceData, ourSetName = null) {
  const { error } = await supabase
    .from('poketrace_price_cache')
    .upsert({
      poketrace_id: poketraceId,
      card_name: cardName,
      set_name: setName || null,
      // Which of OUR sets asked. `set_name` above is the vendor's, which is a
      // different question and cannot be used to key the lookup: a cached Base
      // Set 2 Charizard answered every request for Base Set, because the two
      // share a card name and a leading number.
      our_set_name: ourSetName || null,
      card_number: cardNumber || null,
      price_data: priceData,
      fetched_at: new Date().toISOString(),
    }, { onConflict: 'poketrace_id' });

  if (error) {
    console.error('[Poketrace] Cache save error:', error);
  }
}

// ---- Public API ----

/**
 * Get pricing for a card. Checks Supabase cache first, then fetches from Poketrace.
 *
 * @param {Object} params
 * @param {string} params.cardName - Card name (e.g. "Charizard")
 * @param {string} [params.cardNumber] - Card number (e.g. "004/102")
 * @param {string} [params.setName] - Set name (e.g. "Base Set")
 * @param {string} [params.grade] - User's grade/condition (e.g. "PSA 10", "NM")
 * @param {boolean} [params.forceRefresh] - Skip cache, fetch fresh from API
 * @returns {Object|null} Price data or null
 */
export async function getCardPrice({ cardName, cardNumber, setName, variant, grade, forceRefresh = false }) {
  if (!cardName) return null;
  console.log('[Poketrace] getCardPrice called:', { cardName, cardNumber, setName, grade });

  try {
    // 1. Check Supabase cache (unless force refresh)
    if (!forceRefresh) {
      const cached = await getCachedPrice(cardName, cardNumber, variant, setName);
      if (cached) {
        console.log('[Poketrace] Cache hit for', cardName, '- has prices:', !!cached.price_data?.prices);
        return formatPriceResult(cached.price_data, grade, cached.fetched_at);
      }
    }

    // 2. Search Poketrace for this card
    const searchResult = await searchPoketraceCard(cardName, cardNumber, setName, variant);
    console.log('[Poketrace] Search result for', cardName, ':', searchResult ? searchResult.id : 'null');
    if (!searchResult) return null;

    const poketraceId = searchResult.id;

    // 3. Check cache by poketrace_id (in case name/number didn't match but ID exists)
    if (!forceRefresh) {
      const cachedById = await getCachedPriceById(poketraceId);
      if (cachedById) {
        console.log('[Poketrace] Cache hit by ID for', cardName);
        return formatPriceResult(cachedById.price_data, grade, cachedById.fetched_at);
      }
    }

    // 4. Fetch full pricing from Poketrace
    const priceData = await fetchPoketracePricing(poketraceId);
    console.log('[Poketrace] Pricing data for', cardName, ':', priceData ? { hasPrices: !!priceData.prices, keys: Object.keys(priceData.prices || {}) } : 'null');
    if (!priceData) return null;

    // 5. Save to Supabase cache
    await savePriceToCache(
      poketraceId,
      cardName,
      priceData.set?.name || setName,
      priceData.cardNumber || cardNumber,
      priceData,
      setName
    );

    return formatPriceResult(priceData, grade, new Date().toISOString());
  } catch (err) {
    console.error('[Poketrace] getCardPrice error:', err);

    // On network error, try to return stale cache
    const cached = await getCachedPrice(cardName, cardNumber, variant, setName);
    if (cached) {
      return formatPriceResult(cached.price_data, grade, cached.fetched_at);
    }
    return null;
  }
}

/**
 * Get all pricing tiers for a card (for the graded price modal).
 * Returns the full breakdown of graded + raw prices.
 *
 * @param {Object} params
 * @param {string} params.cardName
 * @param {string} [params.cardNumber]
 * @param {string} [params.setName]
 * @param {string} [params.source] - "ebay" or "tcgplayer" (default: "ebay")
 * @param {boolean} [params.forceRefresh]
 * @param {Object} [params.reportCard] - the card as displayed. When present, a
 *        failure to price is logged to the pricing repair loop with the gate
 *        that rejected it. Reporting is fire-and-forget and never blocks.
 * @returns {Object|null}
 */
export async function getAllPriceTiers({ cardName, cardNumber, setName, variant, source = 'ebay', forceRefresh = false, reportCard = null, onFailure = null, gradedOnly = false }) {
  if (!cardName) return null;

  // reportCard sends the failure to the repair loop. onFailure hands the stage
  // back to the caller, which is how the price cache knows what to record and
  // when to bother trying again.
  const report = (stage, diagnostics) => {
    if (reportCard) reportPricingFailure(reportCard, stage, diagnostics);
    if (onFailure) { try { onFailure(stage, diagnostics); } catch (e) { /* never break pricing */ } }
  };

  try {
    let priceData = null;
    let tieBrokenInfo = null;

    if (!forceRefresh) {
      const cached = await getCachedPrice(cardName, cardNumber, variant, setName);
      if (cached) {
        // Unwrap if cached with old { data: { ... } } format
        priceData = cached.price_data?.data || cached.price_data;
      }
    }

    if (!priceData) {
      const { card: searchResult, failure, tieBroken } = await searchPoketraceCardDetailed(cardName, cardNumber, setName, variant);
      tieBrokenInfo = tieBroken || null;
      if (!searchResult) {
        report(failure?.stage || FAILURE_STAGES.SEARCH_EMPTY, failure?.diagnostics || {});
        return null;
      }

      priceData = await fetchPoketracePricing(searchResult.id);
      if (!priceData) {
        report(FAILURE_STAGES.NO_PRICE_DATA, { poketraceId: searchResult.id, matchedSet: searchResult.set?.name });
        return null;
      }

      await savePriceToCache(
        searchResult.id,
        cardName,
        priceData.set?.name || setName,
        priceData.cardNumber || cardNumber,
        priceData,
        setName
      );
    }

    // Prefer the market asked for, then fall back through the others.
    //
    // eBay sold data lags a release by months, so a set that came out this
    // quarter has a TCGplayer ask and nothing else. Refusing to price it left
    // every new set blank, which is exactly when people are scanning most.
    //
    // The sources mean different things - eBay is what cards sold for,
    // TCGplayer is what they are listed at - so the answer records which one
    // replied rather than blending them.
    // Graded cards are eBay or nothing. TCGplayer lists raw cards for sale and
    // carries no graded tiers whatever: asked for this card it returns Near
    // Mint through Damaged and not a single PSA, CGC or TAG grade. Falling
    // through to it would find no matching grade, price the slab at nothing,
    // and then the retry backoff would write the card off for a month.
    const FALLBACK_ORDER = gradedOnly ? ['ebay'] : [source, 'ebay', 'tcgplayer', 'cardmarket'];
    let prices = {};
    let pricedFrom = source;
    for (const candidate of FALLBACK_ORDER) {
      const rows = priceData.prices?.[candidate] || {};
      if (Object.keys(rows).length > 0) {
        prices = rows;
        pricedFrom = candidate;
        break;
      }
    }

    // Nothing on any market. A clean match with zero rows everywhere is what a
    // wrong-market match looks like from the inside.
    if (Object.keys(prices).length === 0) {
      report(FAILURE_STAGES.EMPTY_TIERS, {
        matchedSet: priceData.set?.name,
        matchedVariant: priceData.variant,
        market: priceData.market,
        currency: priceData.currency,
        wantedSource: source,
        availableSources: Object.keys(priceData.prices || {}),
      });
      return null;
    }

    // Split into graded and raw
    const graded = {};
    const raw = {};

    for (const [tier, data] of Object.entries(prices)) {
      if (RAW_TIERS.includes(tier)) {
        raw[tier] = data;
      } else {
        graded[tier] = data;
      }
    }

    // Sort graded tiers
    const sortedGraded = Object.entries(graded)
      .sort(([a], [b]) => {
        const ai = GRADE_SORT.indexOf(a);
        const bi = GRADE_SORT.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      })
      .reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});

    // Sort raw tiers
    const sortedRaw = Object.entries(raw)
      .sort(([a], [b]) => RAW_TIERS.indexOf(a) - RAW_TIERS.indexOf(b))
      .reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});

    return {
      cardName: priceData.name || cardName,
      setName: priceData.set?.name || setName,
      cardNumber: priceData.cardNumber || cardNumber,
      // The resolved Poketrace id, so a caller that wants the individual sales
      // behind these aggregates can ask for them without repeating the match.
      cardId: priceData.id || null,
      tcgplayerId: priceData.refs?.tcgplayerId || null,
      image: priceData.image,
      market: priceData.market,
      currency: priceData.currency || 'USD',
      topPrice: priceData.topPrice,
      totalSales: priceData.totalSaleCount,
      source: pricedFrom,
      // What the numbers actually are. eBay figures are completed sales,
      // TCGplayer figures are current asking prices, and a screen that shows
      // one while implying the other is misleading.
      basis: pricedFrom === 'ebay' ? 'sold' : 'asking',
      requestedSource: source,
      graded: sortedGraded,
      raw: sortedRaw,
      // Present only when two printings tied and the plain name won. The screen
      // names the printing it priced so an unusual variant is not silently
      // reported at the common card's price.
      tieBroken: tieBrokenInfo,
    };
  } catch (err) {
    console.error('[Poketrace] getAllPriceTiers error:', err);
    return null;
  }
}

/**
 * Format the price result for a specific grade/condition
 */
function formatPriceResult(priceData, grade, fetchedAt) {
  // Unwrap if cached with old { data: { ... } } format
  const d = priceData?.data || priceData;
  const poketraceKey = gradeToPoketraceKey(grade);
  const graded = grade ? isGradedCard(grade) : false;

  // Default source: ebay, fallback to cardmarket for EU cards
  const ebayPrices = d.prices?.ebay || {};
  const tcgPrices = d.prices?.tcgplayer || {};
  const cmPrices = d.prices?.cardmarket || {};

  let tierData = null;
  let usedFallback = false;

  if (poketraceKey) {
    // Try exact match in eBay data first
    tierData = ebayPrices[poketraceKey];

    // Try TCGPlayer if eBay doesn't have it (raw conditions only)
    if (!tierData && !graded) {
      tierData = tcgPrices[poketraceKey];
    }

    // Try CardMarket (EU cards) - use UNKNOWN tier if specific tier missing
    if (!tierData) {
      tierData = cmPrices[poketraceKey] || cmPrices['UNKNOWN'];
    }

    // Fallback: use NEAR_MINT with multiplier for raw conditions
    if (!tierData && !graded) {
      const nmData = ebayPrices['NEAR_MINT'] || tcgPrices['NEAR_MINT'] || cmPrices['NEAR_MINT'] || cmPrices['UNKNOWN'];
      if (nmData) {
        const multiplier = CONDITION_MULTIPLIERS[poketraceKey] || 0.5;
        tierData = {
          avg: Math.round(nmData.avg * multiplier * 100) / 100,
          low: nmData.low ? Math.round(nmData.low * multiplier * 100) / 100 : null,
          high: nmData.high ? Math.round(nmData.high * multiplier * 100) / 100 : null,
          saleCount: 0,
          trend: nmData.trend,
          confidence: 'estimated',
        };
        usedFallback = true;
      }
    }
  }

  return {
    cardName: d.name,
    setName: d.set?.name,
    cardNumber: d.cardNumber,
    image: d.image,
    market: d.market,
    currency: d.currency || 'USD',
    topPrice: d.topPrice,
    grade,
    poketraceKey,
    // Flatten for easy consumption
    price: tierData?.avg || null,
    low: tierData?.low || null,
    high: tierData?.high || null,
    saleCount: tierData?.saleCount || 0,
    trend: tierData?.trend || null,
    confidence: tierData?.confidence || null,
    median3d: tierData?.median3d || null,
    median7d: tierData?.median7d || null,
    median30d: tierData?.median30d || null,
    usedFallback,
    fetchedAt,
    // Keep full data for modal drill-down
    allPrices: d.prices,
  };
}

/**
 * Format a Poketrace tier key to a human-readable label
 * e.g. "PSA_10" -> { company: "PSA", grade: "10" }
 * e.g. "CGC_9_5" -> { company: "CGC", grade: "9.5" }
 * e.g. "NEAR_MINT" -> { company: null, grade: "Near Mint" }
 */
export function formatTierLabel(tier) {
  if (RAW_TIERS.includes(tier)) {
    return {
      company: null,
      grade: tier.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()),
    };
  }

  const parts = tier.split('_');
  const company = parts[0];
  const grade = parts.slice(1).join('.');
  return { company, grade };
}

/**
 * Format a price as USD currency string
 */
export function formatPrice(price) {
  if (price === null || price === undefined) return null;
  if (price < 1) return `$${price.toFixed(2)}`;
  if (price < 10) return `$${price.toFixed(2)}`;
  if (price < 100) return `$${price.toFixed(0)}`;
  if (price < 1000) return `$${price.toFixed(0)}`;
  return `$${(price / 1000).toFixed(1)}k`;
}

/**
 * Format price for detail view (always show cents)
 */
export function formatPriceDetail(price) {
  if (price === null || price === undefined) return '--';
  return `$${price.toFixed(2)}`;
}

/**
 * Get full Poketrace pricing for Chef's Lab.
 * Returns the complete pricing object with all graded + raw tiers.
 * Checks poketrace_price_cache first, then fetches from API.
 */
export async function getFullCardPricing({ cardName, cardNumber, setName, variant }) {
  const perfLabel = `[Perf] getFullCardPricing: ${cardName}`;
  console.time(perfLabel);
  if (!cardName) { console.timeEnd(perfLabel); return null; }

  try {
    // Check cache first
    const cached = await getCachedPrice(cardName, cardNumber, variant, setName);
    if (cached) {
      const d = cached.price_data?.data || cached.price_data;
      if (d?.prices) {
        console.log('[Perf] getFullCardPricing: CACHE HIT (poketrace_price_cache)');
        console.timeEnd(perfLabel);
        return { priceData: d, poketraceId: cached.poketrace_id || null };
      }
    }

    // Search Poketrace (with variant for 1st Edition / Shadowless matching)
    const searchResult = await searchPoketraceCard(cardName, cardNumber, setName, variant);
    if (!searchResult) return null;

    const poketraceId = searchResult.id;

    // Check cache by ID
    const cachedById = await getCachedPriceById(poketraceId);
    if (cachedById) {
      const d = cachedById.price_data?.data || cachedById.price_data;
      if (d?.prices) {
        console.log('[Perf] getFullCardPricing: CACHE HIT (by poketraceId)');
        console.timeEnd(perfLabel);
        return { priceData: d, poketraceId };
      }
    }

    // Fetch fresh
    const priceData = await fetchPoketracePricing(poketraceId);
    if (!priceData) return null;

    await savePriceToCache(
      poketraceId,
      cardName,
      priceData.set?.name || setName,
      priceData.cardNumber || cardNumber,
      priceData,
      setName
    );

    console.timeEnd(perfLabel);
    return { priceData, poketraceId };
  } catch (err) {
    console.error('[Poketrace] getFullCardPricing error:', err);
    console.timeEnd(perfLabel);
    return null;
  }
}

// Map raw condition strings from listings API to our short keys
const LISTING_CONDITION_MAP = {
  'near_mint': 'nm',
  'near mint': 'nm',
  'nm': 'nm',
  'lightly_played': 'lp',
  'lightly played': 'lp',
  'lp': 'lp',
  'moderately_played': 'mp',
  'moderately played': 'mp',
  'mp': 'mp',
  'heavily_played': 'hp',
  'heavily played': 'hp',
  'hp': 'hp',
  'damaged': 'dmg',
  'dmg': 'dmg',
};

function conditionToKey(condition) {
  if (!condition) return 'nm';
  return LISTING_CONDITION_MAP[condition.toLowerCase()] || 'nm';
}

/**
 * Transform Poketrace pricing data into Chef's Lab format.
 * If individual listings are provided, evidence comes from those instead of aggregate tiers.
 * graded_data and raw_data are always built from aggregate tier data for quick lookup.
 *
 * @param {Object} poketraceData - Aggregate pricing data from Poketrace
 * @param {Array} [listings] - Individual sold listings from /listings endpoint
 * @returns {Object} { graded_data, raw_data, evidence, card_info }
 */
export function transformForChefsLab(poketraceData, listings) {
  if (!poketraceData?.prices) return null;

  const ebay = poketraceData.prices.ebay || {};
  const tcg = poketraceData.prices.tcgplayer || {};
  const cm = poketraceData.prices.cardmarket || {};

  const graded_data = {};
  const raw_data = {};

  // Raw condition mapping
  const RAW_MAP = {
    'NEAR_MINT': 'nm',
    'LIGHTLY_PLAYED': 'lp',
    'MODERATELY_PLAYED': 'mp',
    'HEAVILY_PLAYED': 'hp',
    'DAMAGED': 'dmg',
  };

  // Always build graded_data and raw_data from aggregate tiers
  for (const [tier, data] of Object.entries(ebay)) {
    if (RAW_MAP[tier]) {
      raw_data[RAW_MAP[tier]] = data.avg;
    } else {
      const parts = tier.split('_');
      const company = parts[0].toLowerCase();
      const gradeNum = parts.slice(1).join('.');
      if (!graded_data[company]) graded_data[company] = {};
      graded_data[company][gradeNum] = data.avg;
    }
  }

  // Fill in TCGPlayer raw prices as fallback
  for (const [tier, data] of Object.entries(tcg)) {
    if (RAW_MAP[tier] && !raw_data[RAW_MAP[tier]]) {
      raw_data[RAW_MAP[tier]] = data.avg;
    }
  }

  // Fill in CardMarket as last fallback
  for (const [tier, data] of Object.entries(cm)) {
    if (RAW_MAP[tier] && !raw_data[RAW_MAP[tier]]) {
      raw_data[RAW_MAP[tier]] = data.avg;
    }
  }

  // Build evidence: individual listings + aggregate fallback for grades not covered
  let evidence = [];

  if (listings && listings.length > 0) {
    // Start with individual listings
    evidence = listings.map(listing => ({
      company: listing.grader?.toLowerCase() || 'raw',
      grade_number: listing.grade
        ? (parseFloat(listing.grade) || listing.grade)
        : conditionToKey(listing.condition),
      price: listing.price,
      title: listing.title,
      url: listing.listingUrl,
      date: listing.soldAt ? listing.soldAt.split('T')[0] : null,
      source: 'ebay',
      anomalyFlag: listing.anomalyFlag || false,
      anomalyReason: listing.anomalyReason || null,
      listingType: listing.listingType,
      condition: listing.condition,
      currency: listing.currency,
    }));

    // Track which company+grade combos are covered by individual listings
    const coveredKeys = new Set();
    for (const e of evidence) {
      coveredKeys.add(`${e.company}_${e.grade_number}`);
    }

    // Fill in aggregate tiers for grades NOT covered by individual listings
    for (const [tier, data] of Object.entries(ebay)) {
      if (RAW_MAP[tier]) {
        const key = `raw_${RAW_MAP[tier]}`;
        if (!coveredKeys.has(key)) {
          evidence.push({
            company: 'raw',
            grade_number: RAW_MAP[tier],
            price: data.avg,
            low: data.low,
            high: data.high,
            date: data.lastUpdated ? data.lastUpdated.split('T')[0] : null,
            saleCount: data.saleCount || 0,
            trend: data.trend,
            confidence: data.confidence,
            median3d: data.median3d,
            median7d: data.median7d,
            median30d: data.median30d,
            source: 'ebay',
            isAggregate: true,
          });
        }
      } else {
        const parts = tier.split('_');
        const company = parts[0].toLowerCase();
        const gradeNum = parseFloat(parts.slice(1).join('.')) || parts.slice(1).join('.');
        const key = `${company}_${gradeNum}`;
        if (!coveredKeys.has(key)) {
          evidence.push({
            company,
            grade_number: gradeNum,
            price: data.avg,
            low: data.low,
            high: data.high,
            date: data.lastUpdated ? data.lastUpdated.split('T')[0] : null,
            saleCount: data.saleCount || 0,
            trend: data.trend,
            confidence: data.confidence,
            median3d: data.median3d,
            median7d: data.median7d,
            median30d: data.median30d,
            source: 'ebay',
            isAggregate: true,
          });
        }
      }
    }
  } else {
    // No individual listings: build evidence entirely from aggregate tiers
    for (const [tier, data] of Object.entries(ebay)) {
      if (RAW_MAP[tier]) {
        evidence.push({
          company: 'raw',
          grade_number: RAW_MAP[tier],
          price: data.avg,
          low: data.low,
          high: data.high,
          date: data.lastUpdated ? data.lastUpdated.split('T')[0] : null,
          saleCount: data.saleCount || 0,
          trend: data.trend,
          confidence: data.confidence,
          median3d: data.median3d,
          median7d: data.median7d,
          median30d: data.median30d,
          source: 'ebay',
        });
      } else {
        const parts = tier.split('_');
        const company = parts[0].toLowerCase();
        const gradeNum = parts.slice(1).join('.');
        evidence.push({
          company,
          grade_number: parseFloat(gradeNum) || gradeNum,
          price: data.avg,
          low: data.low,
          high: data.high,
          date: data.lastUpdated ? data.lastUpdated.split('T')[0] : null,
          saleCount: data.saleCount || 0,
          trend: data.trend,
          confidence: data.confidence,
          median3d: data.median3d,
          median7d: data.median7d,
          median30d: data.median30d,
          source: 'ebay',
        });
      }
    }
  }

  return {
    graded_data,
    raw_data,
    evidence,
    card_info: {
      name: poketraceData.name,
      set: poketraceData.set?.name,
      number: poketraceData.cardNumber,
      image: poketraceData.image,
      topPrice: poketraceData.topPrice,
      totalSaleCount: poketraceData.totalSaleCount,
      hasGraded: poketraceData.hasGraded,
      gradedOptions: poketraceData.gradedOptions,
      conditionOptions: poketraceData.conditionOptions,
    },
  };
}

// ---- Multi-Source Pricing ----

// There is no key in this file and no key in the bundle. Both Gemini calls
// below go through the `gemini-proxy` edge function, which holds the key in
// project secrets, checks that a real user is asking, and counts the call
// against that user's budget. See src/lib/geminiProxy.js for why.

/**
 * Extract per-condition raw data from full Poketrace pricing.
 * Returns { raw, evidence, meta } with detailed breakdown.
 */
function extractPoketraceRaw(poketraceData) {
  if (!poketraceData?.prices) return null;

  const ebay = poketraceData.prices.ebay || {};
  const tcg = poketraceData.prices.tcgplayer || {};
  const cm = poketraceData.prices.cardmarket || {};

  const RAW_MAP = {
    'NEAR_MINT': 'nm',
    'LIGHTLY_PLAYED': 'lp',
    'MODERATELY_PLAYED': 'mp',
    'HEAVILY_PLAYED': 'hp',
    'DAMAGED': 'dmg',
  };

  const raw = {};
  const evidence = [];

  for (const [tier, data] of Object.entries(ebay)) {
    if (RAW_MAP[tier]) {
      const key = RAW_MAP[tier];
      raw[key] = {
        avg: data.avg || null,
        low: data.low || null,
        high: data.high || null,
        saleCount: data.saleCount || 0,
        trend: data.trend || null,
        confidence: data.confidence || null,
        median3d: data.median3d || null,
        median7d: data.median7d || null,
        median30d: data.median30d || null,
        source: 'ebay',
      };
      evidence.push({
        company: 'raw',
        grade_number: key,
        price: data.avg,
        low: data.low,
        high: data.high,
        saleCount: data.saleCount || 0,
        trend: data.trend,
        source: 'ebay',
      });
    }
  }

  // Fill from TCGPlayer as fallback
  for (const [tier, data] of Object.entries(tcg)) {
    const key = RAW_MAP[tier];
    if (key && !raw[key]) {
      raw[key] = {
        avg: data.avg || null,
        low: data.low || null,
        high: data.high || null,
        saleCount: data.saleCount || 0,
        trend: data.trend || null,
        confidence: data.confidence || null,
        source: 'tcgplayer',
      };
    }
  }

  // Fill from CardMarket as last fallback
  for (const [tier, data] of Object.entries(cm)) {
    const key = RAW_MAP[tier];
    if (key && !raw[key]) {
      raw[key] = {
        avg: data.avg || null,
        low: data.low || null,
        high: data.high || null,
        saleCount: data.saleCount || 0,
        source: 'cardmarket',
      };
    }
  }

  return {
    raw,
    evidence,
    meta: {
      totalSaleCount: poketraceData.totalSaleCount || 0,
      topPrice: poketraceData.topPrice || null,
      currency: poketraceData.currency || 'USD',
      market: poketraceData.market || null,
    },
  };
}

/**
 * Call Gemini to analyze pricing from multiple sources.
 */
async function callGeminiPricingAnalysis({ cardName, cardNumber, setName, variant, poketrace, tcgdex, justTCG, listings }) {
  const perfLabel = `[Perf] callGeminiPricingAnalysis: ${cardName}`;
  console.time(perfLabel);
  try {
    const pt = poketrace?.raw || {};
    const tcg = tcgdex?.tcgplayer || {};
    const cm = tcgdex?.cardmarket || {};

    const fmtCond = (key) => {
      const d = pt[key];
      if (!d?.avg) return 'N/A';
      return `$${d.avg.toFixed(2)} avg (${d.saleCount || 0} sales${d.low ? `, range $${d.low.toFixed(2)}-$${d.high?.toFixed(2) || '?'}` : ''})`;
    };

    const tcgVariant = tcg.holofoil || tcg.normal || tcg.reverseHolofoil || {};
    const fmtTcg = (field) => tcgVariant[field] ? `$${tcgVariant[field].toFixed(2)}` : 'N/A';

    // Build JustTCG section for prompt
    let justTCGSection = '';
    if (justTCG?.conditions) {
      const jc = justTCG.conditions;
      const fmtJ = (key) => {
        const d = jc[key];
        if (!d?.price) return 'N/A';
        let s = `$${d.price.toFixed(2)}`;
        if (d.change7d != null) s += ` (7d: ${d.change7d >= 0 ? '+' : ''}${d.change7d.toFixed(1)}%)`;
        return s;
      };
      justTCGSection = `\nSOURCE 3 - JustTCG (TCGPlayer marketplace by condition${justTCG.variant ? `, ${justTCG.variant}` : ''}):
  NM: ${fmtJ('nm')}
  LP: ${fmtJ('lp')}
  MP: ${fmtJ('mp')}
  HP: ${fmtJ('hp')}
  DMG: ${fmtJ('dmg')}`;
      const nmData = jc.nm;
      if (nmData?.change30d != null) {
        justTCGSection += `\n  30d trend: ${nmData.change30d >= 0 ? '+' : ''}${nmData.change30d.toFixed(1)}% (NM)`;
      }
      justTCGSection += '\n';
    }

    // Build recent sold listings section from individual listings
    let recentSalesSection = '';
    if (listings && listings.length > 0) {
      const sortedListings = [...listings]
        .filter(l => l.price && l.soldAt)
        .sort((a, b) => new Date(b.soldAt) - new Date(a.soldAt));

      // Group by grader+grade or condition, show up to 5 recent per group
      const groups = {};
      for (const l of sortedListings) {
        const key = l.grader
          ? `${l.grader} ${l.grade}`
          : (l.condition || 'Unknown');
        if (!groups[key]) groups[key] = [];
        if (groups[key].length < 5) {
          const dateStr = l.soldAt.split('T')[0];
          groups[key].push(`$${l.price.toFixed(2)} (${dateStr})`);
        }
      }

      const lines = Object.entries(groups)
        .map(([key, sales]) => `  ${key}: ${sales.join(', ')}`)
        .join('\n');

      recentSalesSection = `\nRECENT INDIVIDUAL SOLD LISTINGS (${listings.length} total):
${lines}\n`;
    }

    const prompt = `You are a Pokemon TCG pricing analyst and card market expert. Given market data from multiple sources, evaluate data quality, estimate fair raw condition price RANGES, and provide rich card market intelligence.

CARD: ${cardName} #${cardNumber || '?'} from ${setName || 'Unknown'} (${variant || 'Normal'})

SOURCE 1 - Poketrace (eBay/TCGPlayer sold data by condition):
  NM: ${fmtCond('nm')}
  LP: ${fmtCond('lp')}
  MP: ${fmtCond('mp')}
  HP: ${fmtCond('hp')}
  DMG: ${fmtCond('dmg')}

SOURCE 2 - TCGdex (market listings, ~NM equivalent):
  TCGPlayer Market: ${fmtTcg('market')}
  TCGPlayer Low: ${fmtTcg('low')}
  TCGPlayer High: ${fmtTcg('high')}
  CardMarket Avg: ${cm.avg ? `EUR ${cm.avg.toFixed(2)} (~USD ${cm.avgUSD?.toFixed(2) || '?'})` : 'N/A'}
  CardMarket 30d Avg: ${cm.avg30 ? `EUR ${cm.avg30.toFixed(2)}` : 'N/A'}
${justTCGSection}${recentSalesSection}
TASKS:

0. DATA QUALITY CHECK - Before pricing, evaluate the source data:
   - Do these listings match the actual card? Check name/set/number alignment
   - Are there enough sales for reliable pricing?
   - Do the sources agree, or is there suspicious discrepancy?
   - IMPORTANT: JustTCG (SOURCE 3) provides VARIANT-SPECIFIC pricing (e.g., "1st Edition Holofoil"). TCGdex (SOURCE 2) provides GENERIC market averages that may reflect a DIFFERENT variant or region (e.g., Unlimited, European market). If JustTCG has per-condition data for the correct variant, treat the data as "good" even if TCGdex/CardMarket prices differ -- that discrepancy is expected (variant/region mismatch, not bad data).
   - Return data_quality: "good", "suspicious", or "insufficient"
   - If not good, explain why in data_quality_reasoning

1. PRICE ESTIMATES - For each condition (NM, LP, MP, HP, DMG):
   - Provide a realistic USD price RANGE (low and high)
   - Be confident, not conservative -- reflect actual market spread
   - The high end should represent what a seller can reasonably expect
   - The low end should represent a fair buy price
   - Weight JustTCG per-condition prices most heavily when available (variant-specific TCGPlayer data). Then Poketrace. For missing conditions use standard multipliers (LP ~80%, MP ~60%, HP ~40%, DMG ~25% of NM)
   - One-sentence reasoning

2. MARKET INTELLIGENCE SUMMARY - Write a 4-6 sentence market brief covering ALL of these:
   - Card Identity: What this card is and why it matters (chase card? vintage staple? competitive use? iconic artwork?)
   - Demand Drivers: What drives demand - nostalgia, set popularity, grading pop reports, variant scarcity, competitive play
   - Market Trend: Is this card trending up, down, or stable? Any recent price spikes or drops?
   - Pricing Analysis: How the two sources compare and what the spread tells us
   - Trade Perspective: Brief take on whether this is a good hold, buy, or sell right now

Return JSON:
{
  "data_quality": "good",
  "data_quality_reasoning": "Both sources agree and have sufficient sales data",
  "conditions": {
    "nm": { "low": 40.00, "high": 50.00, "reasoning": "..." },
    "lp": { "low": 32.00, "high": 40.00, "reasoning": "..." },
    "mp": { "low": 24.00, "high": 30.00, "reasoning": "..." },
    "hp": { "low": 16.00, "high": 20.00, "reasoning": "..." },
    "dmg": { "low": 10.00, "high": 12.50, "reasoning": "..." }
  },
  "summary": "4-6 sentence market brief covering card identity, demand drivers, pricing analysis, market trend, and trade perspective.",
  "confidence": "high"
}`;

    console.log('[MultiSource] Calling Gemini pricing analysis...');

    const res = await geminiGenerate({
      model: 'gemini-2.5-flash',
      contents: [{ parts: [{ text: prompt }] }],
      generationConfig: {
        temperature: 0.2,
        maxOutputTokens: 8192,
        responseMimeType: 'application/json',
      },
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('[MultiSource] Gemini API error:', res.status, errText);
      console.timeEnd(perfLabel);
      return null;
    }

    const data = await res.json();
    console.log('[MultiSource] Gemini response candidates:', data.candidates?.length || 0);

    if (!data.candidates || data.candidates.length === 0) {
      console.error('[MultiSource] Gemini returned no candidates:', JSON.stringify(data).slice(0, 500));
      console.timeEnd(perfLabel);
      return null;
    }

    const parts = data.candidates[0]?.content?.parts || [];
    const textPart = parts.find(p => !p.thought && p.text) || parts[0];

    if (!textPart?.text) {
      console.error('[MultiSource] Gemini no text in response parts:', parts.length);
      console.timeEnd(perfLabel);
      return null;
    }

    console.log('[MultiSource] Gemini raw text:', textPart.text.slice(0, 200));

    // Parse JSON - strip markdown fences if present
    let jsonStr = textPart.text.trim();
    jsonStr = jsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const parsed = JSON.parse(jsonStr);

    console.log('[MultiSource] Data quality:', parsed.data_quality, parsed.data_quality_reasoning || '');

    console.timeEnd(perfLabel);
    return {
      raw: parsed.conditions || {},
      data_quality: parsed.data_quality || 'good',
      data_quality_reasoning: parsed.data_quality_reasoning || '',
      summary: parsed.summary || '',
      confidence: parsed.confidence || 'medium',
      model: 'gemini-2.5-flash',
      usedGrounding: false,
      fetchedAt: new Date().toISOString(),
    };
  } catch (err) {
    console.error('[MultiSource] Gemini pricing analysis error:', err.message || err);
    console.timeEnd(perfLabel);
    return null;
  }
}

/**
 * Call Gemini WITH Google Search grounding when data quality is bad.
 * Uses tools: [{ google_search: {} }] which is incompatible with responseMimeType.
 * Extracts JSON from text response manually.
 */
async function callGeminiWithGrounding({ cardName, cardNumber, setName, variant, dataQualityReason }) {
  const perfLabel = `[Perf] callGeminiWithGrounding: ${cardName}`;
  console.time(perfLabel);
  try {
    const prompt = `You are a Pokemon TCG pricing analyst. The automated data sources provided UNRELIABLE data for this card.
Reason: ${dataQualityReason}

CARD: ${cardName} #${cardNumber || '?'} from ${setName || 'Unknown'} (${variant || 'Normal'})

Use Google Search to find current market data from:
- PriceCharting.com, eBay sold listings, PSA auction prices, TCGPlayer

Explain what was wrong with the original data and what you found instead.

For each condition (NM, LP, MP, HP, DMG), provide a realistic USD price RANGE (low and high).
Be confident -- reflect actual market reality. The high end should represent what a seller can expect.

Return JSON (and ONLY JSON, no other text):
{
  "data_quality": "corrected",
  "data_quality_reasoning": "Explain what was wrong and what you found",
  "conditions": {
    "nm": { "low": 1800, "high": 2200, "reasoning": "..." },
    "lp": { "low": 1200, "high": 1500, "reasoning": "..." },
    "mp": { "low": 800, "high": 1000, "reasoning": "..." },
    "hp": { "low": 500, "high": 700, "reasoning": "..." },
    "dmg": { "low": 300, "high": 450, "reasoning": "..." }
  },
  "summary": "Market brief with what you found from web search",
  "confidence": "high"
}`;

    console.log('[MultiSource] Calling Gemini with grounding...');

    const res = await geminiGenerate({
      model: 'gemini-2.5-flash',
      contents: [{ parts: [{ text: prompt }] }],
      tools: [{ google_search: {} }],
      generationConfig: {
        temperature: 0.2,
        maxOutputTokens: 8192,
      },
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('[MultiSource] Gemini grounding API error:', res.status, errText);
      console.timeEnd(perfLabel);
      return null;
    }

    const data = await res.json();

    if (!data.candidates || data.candidates.length === 0) {
      console.error('[MultiSource] Gemini grounding returned no candidates');
      console.timeEnd(perfLabel);
      return null;
    }

    const parts = data.candidates[0]?.content?.parts || [];
    const textPart = parts.find(p => !p.thought && p.text) || parts[0];

    if (!textPart?.text) {
      console.error('[MultiSource] Gemini grounding no text in response');
      console.timeEnd(perfLabel);
      return null;
    }

    console.log('[MultiSource] Gemini grounding raw text:', textPart.text.slice(0, 300));

    // Extract JSON from text (no responseMimeType with tools)
    let jsonStr = textPart.text.trim();
    jsonStr = jsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('[MultiSource] Gemini grounding: no JSON found in response');
      console.timeEnd(perfLabel);
      return null;
    }

    const parsed = JSON.parse(jsonMatch[0]);

    // Extract grounding metadata if available
    const groundingMeta = data.candidates[0]?.groundingMetadata || {};
    const searchQueries = groundingMeta.webSearchQueries || [];
    const groundingSources = groundingMeta.groundingChunks?.map(c => ({
      url: c.web?.uri,
      title: c.web?.title,
    })).filter(s => s.url) || [];

    console.log('[MultiSource] Grounding sources:', groundingSources.length, 'queries:', searchQueries);

    console.timeEnd(perfLabel);
    return {
      raw: parsed.conditions || {},
      data_quality: parsed.data_quality || 'corrected',
      data_quality_reasoning: parsed.data_quality_reasoning || dataQualityReason,
      groundingReason: dataQualityReason,
      summary: parsed.summary || '',
      confidence: parsed.confidence || 'medium',
      model: 'gemini-2.5-flash',
      usedGrounding: true,
      groundingMetadata: {
        searchQueries,
        sources: groundingSources,
      },
      fetchedAt: new Date().toISOString(),
    };
  } catch (err) {
    console.error('[MultiSource] Gemini grounding error:', err.message || err);
    console.timeEnd(perfLabel);
    return null;
  }
}

/**
 * Compute the "best" single price from all sources.
 * Priority: Gemini (AI analysis of all data) > Poketrace > TCGdex.
 */
function computeBestPrice(poketrace, tcgdex, gemini) {
  // Gemini-only. No fallback. If Gemini fails, return null (tile shows "?").
  const gmNm = gemini?.raw?.nm;
  if (gmNm && (gmNm.high || gmNm.price)) {
    const high = gmNm.high || gmNm.price;
    const low = gmNm.low || gmNm.price;
    return {
      price: high,
      priceLow: low,
      priceHigh: high,
      source: 'gemini',
      condition: 'NM',
      reasoning: gmNm.reasoning || 'AI-analyzed price from all sources',
      isRange: !!(gmNm.low && gmNm.high),
    };
  }

  return null;
}

/**
 * Gemini-first pricing pipeline for Fair Trade tiles.
 * Fast path: Poketrace first (need tcgplayerId), then JustTCG + TCGdex in parallel, then Gemini.
 * If data quality is bad, calls Gemini again with Google Search grounding.
 * Returns { bestPrice, gemini, poketrace, tcgdex, justTCG, poketraceId }
 */
export async function getGeminiPrimaryPrice({ cardName, cardNumber, setName, tcgdexCardId, variant }) {
  const perfLabel = `[Perf] getGeminiPrimaryPrice: ${cardName}`;
  console.time(perfLabel);
  console.log('[GeminiPrimary] Starting for:', cardName);

  // 1. Poketrace first (we need tcgplayerId from its response for JustTCG)
  let poketraceResult = null;
  try {
    poketraceResult = await getFullCardPricing({ cardName, cardNumber, setName, variant });
  } catch (err) {
    console.error('[GeminiPrimary] Poketrace error:', err);
  }

  const poketraceRaw = poketraceResult?.priceData || null;
  const poketraceId = poketraceResult?.poketraceId || null;
  const tcgplayerId = poketraceRaw?.refs?.tcgplayerId || poketraceRaw?.tcgplayerId || null;

  // 2. Parallel: TCGdex + JustTCG (JustTCG needs tcgplayerId from step 1)
  const [tcgdexSettled, justTCGSettled] = await Promise.allSettled([
    tcgdexCardId ? fetchTCGdexPricing(tcgdexCardId) : Promise.resolve(null),
    tcgplayerId ? fetchJustTCGPricing(tcgplayerId, variant) : Promise.resolve(null),
  ]);

  const tcgdex = tcgdexSettled.status === 'fulfilled' ? tcgdexSettled.value : null;
  const justTCG = justTCGSettled.status === 'fulfilled' ? justTCGSettled.value : null;

  // 3. Transform Poketrace into per-condition breakdown
  const poketrace = extractPoketraceRaw(poketraceRaw);

  console.log('[GeminiPrimary] Poketrace:', poketrace ? Object.keys(poketrace.raw).length + ' conditions' : 'null');
  console.log('[GeminiPrimary] TCGdex:', tcgdex ? 'available' : 'null');
  console.log('[GeminiPrimary] JustTCG:', justTCG ? Object.keys(justTCG.conditions).length + ' conditions' : 'null (tcgplayerId: ' + tcgplayerId + ')');

  // 4. Gemini call #1 (no listings -- fast)
  let gemini = await callGeminiPricingAnalysis({
    cardName, cardNumber, setName, variant,
    poketrace, tcgdex, justTCG, listings: null,
  });

  // 5. If data quality is bad, call Gemini with grounding
  if (gemini && gemini.data_quality !== 'good') {
    console.log('[GeminiPrimary] Data quality:', gemini.data_quality, '- calling with grounding...');
    const grounded = await callGeminiWithGrounding({
      cardName, cardNumber, setName, variant,
      dataQualityReason: gemini.data_quality_reasoning || 'Data quality flagged as ' + gemini.data_quality,
    });
    if (grounded) {
      gemini = grounded;
    }
    // If grounding fails, keep the first result
  }

  console.log('[GeminiPrimary] Gemini:', gemini ? `confidence: ${gemini.confidence}, grounded: ${gemini.usedGrounding}` : 'null');

  // 6. Compute best price
  const bestPrice = computeBestPrice(poketrace, tcgdex, gemini);

  console.timeEnd(perfLabel);
  return {
    bestPrice,
    gemini,
    poketrace,
    tcgdex,
    justTCG,
    poketraceId,
  };
}

/**
 * Fetch pricing from all sources for Chef's Lab multi-source raw tab.
 * Poketrace first (need tcgplayerId), then JustTCG + TCGdex + listings in parallel, then Gemini.
 *
 * @param {Object} params
 * @param {string} params.cardName
 * @param {string} [params.cardNumber]
 * @param {string} [params.setName]
 * @param {string} [params.tcgdexCardId] - e.g., "base1-4"
 * @param {string} [params.variant] - e.g., "Holo", "Normal"
 * @returns {Object} Multi-source result { poketrace, tcgdex, justTCG, gemini, bestPrice, fetchedAt }
 */
/**
 * Same sources as getMultiSourcePricing, WITHOUT the Gemini call.
 *
 * The card detail screen needs its pricing read to be there the instant the
 * screen paints. A model round trip is 1-3s, costs money on every card view and
 * can fail, so the per-condition comparison is built from the raw sources and
 * the opinion line is computed from them (see utils/priceRead.js).
 *
 * @returns {{ poketrace, tcgdex, justTCG, fetchedAt } | null}
 */
export async function getSourcesForCard({
  cardName, cardNumber, setName, tcgdexCardId, variant, knownTcgplayerId = null,
}) {
  if (!cardName) return null;

  // Skip the whole first lookup when the caller already has the id.
  //
  // This function existed to find a tcgplayerId, and it found it by resolving
  // the card at the vendor from scratch - the same resolution the ladder had
  // just finished doing. So the TCGplayer column sat behind a duplicate vendor
  // round trip on every card, and when that round trip was slow the column
  // showed nothing at all. `getAllPriceTiers` already returns the id.
  let poketraceResult = null;
  if (!knownTcgplayerId) {
    try {
      poketraceResult = await getFullCardPricing({ cardName, cardNumber, setName, variant });
    } catch (err) {
      console.error('[Sources] Poketrace error:', err.message);
    }
  }

  const poketraceRaw = poketraceResult?.priceData || null;
  const tcgplayerId = knownTcgplayerId
    || poketraceRaw?.refs?.tcgplayerId
    || poketraceRaw?.tcgplayerId
    || null;

  const [tcgdexSettled, justTCGSettled] = await Promise.allSettled([
    tcgdexCardId ? fetchTCGdexPricing(tcgdexCardId) : Promise.resolve(null),
    tcgplayerId ? fetchJustTCGPricing(tcgplayerId, variant) : Promise.resolve(null),
  ]);

  return {
    poketrace: extractPoketraceRaw(poketraceRaw),
    poketraceTiers: poketraceRaw?.prices?.ebay || null,
    tcgdex: tcgdexSettled.status === 'fulfilled' ? tcgdexSettled.value : null,
    justTCG: justTCGSettled.status === 'fulfilled' ? justTCGSettled.value : null,
    fetchedAt: new Date().toISOString(),
  };
}

export async function getMultiSourcePricing({ cardName, cardNumber, setName, tcgdexCardId, variant }) {
  const perfLabel = `[Perf] getMultiSourcePricing: ${cardName}`;
  console.time(perfLabel);
  console.log('[MultiSource] Fetching for:', cardName, { cardNumber, setName, tcgdexCardId, variant });

  // 1. Poketrace first (need tcgplayerId for JustTCG)
  let poketraceResult = null;
  try {
    poketraceResult = await getFullCardPricing({ cardName, cardNumber, setName, variant });
  } catch (err) {
    console.error('[MultiSource] Poketrace error:', err);
  }

  const poketraceRaw = poketraceResult?.priceData || null;
  const poketraceId = poketraceResult?.poketraceId || null;
  const tcgplayerId = poketraceRaw?.refs?.tcgplayerId || poketraceRaw?.tcgplayerId || null;

  // 2. PARALLEL: TCGdex + JustTCG + Listings
  const [tcgdexSettled, justTCGSettled, listingsSettled] = await Promise.allSettled([
    tcgdexCardId ? fetchTCGdexPricing(tcgdexCardId) : Promise.resolve(null),
    tcgplayerId ? fetchJustTCGPricing(tcgplayerId, variant) : Promise.resolve(null),
    poketraceId ? fetchPoketraceListings(poketraceId) : Promise.resolve([]),
  ]);

  const tcgdex = tcgdexSettled.status === 'fulfilled' ? tcgdexSettled.value : null;
  const justTCG = justTCGSettled.status === 'fulfilled' ? justTCGSettled.value : null;
  const listings = listingsSettled.status === 'fulfilled' ? listingsSettled.value : [];

  console.log('[MultiSource] Individual listings:', listings.length);

  // 3. Transform Poketrace into per-condition breakdown
  const poketrace = extractPoketraceRaw(poketraceRaw);

  console.log('[MultiSource] Poketrace:', poketrace ? Object.keys(poketrace.raw).length + ' conditions' : 'null');
  console.log('[MultiSource] TCGdex:', tcgdex ? 'available' : 'null');
  console.log('[MultiSource] JustTCG:', justTCG ? Object.keys(justTCG.conditions).length + ' conditions' : 'null');

  // 4. SEQUENTIAL: Gemini analysis with all sources + individual listings
  const gemini = await callGeminiPricingAnalysis({
    cardName, cardNumber, setName, variant,
    poketrace, tcgdex, justTCG, listings,
  });

  console.log('[MultiSource] Gemini:', gemini ? `confidence: ${gemini.confidence}` : 'null');

  // 5. Compute best price
  const bestPrice = computeBestPrice(poketrace, tcgdex, gemini);

  console.timeEnd(perfLabel);
  return {
    poketrace,
    tcgdex,
    justTCG,
    gemini,
    bestPrice,
    listings,
    fetchedAt: new Date().toISOString(),
  };
}

export { RAW_TIERS, GRADE_SORT, CONDITION_MAP, CONDITION_MULTIPLIERS, LEGACY_CONDITION_MULTIPLIERS };
